/**
 * Line Detection Pipeline
 *
 * Implements a configurable pipeline for line detection processing.
 * Processes image data through multiple stages: grayscale conversion,
 * edge detection, Hough transform, line filtering, and refinement.
 */

import { BasePipeline } from '../../../../utils/BasePipeline';

/**
 * Pipeline Input: Raw image data from viewport capture
 */
export interface LineDetectionInput {
  imageData: ImageData;
  width: number;
  height: number;
  /** Optional position map for pixel-aligned line detection (uses B channel as grayscale input) */
  positionMap?: ImageData;
}

/**
 * Pipeline Output: Detected lines in 2D image space
 */
export interface LineDetectionOutput {
  lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
  debugData: {
    capturedImage: ImageData;
    grayscaleImage: ImageData;
    edgesImage: ImageData;
    rawLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
    filteredLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
  };
}

/**
 * Internal pipeline context: carries image processing state
 */
export interface LineDetectionContext {
  // Original input
  capturedImage: ImageData;
  width: number;
  height: number;
  /** Position map for pixel-aligned line detection (uses B channel as grayscale input) */
  positionMap?: ImageData;

  // Processing stages
  grayscale?: Uint8ClampedArray;
  grayscaleImage?: ImageData;
  edges?: Uint8ClampedArray;
  edgesImage?: ImageData;

  // Line detection results
  rawLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
  filteredLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
}

/**
 * Pipeline pass parameters
 */
export interface LineDetectionKwargs extends Record<string, unknown> {
  // Grayscale conversion (no params currently)

  // Canny edge detection
  cannyLowThreshold?: number;
  cannyHighThreshold?: number;
  cannyDilationSize?: number; // Size of dilation kernel to close gaps between parallel edges

  // Hough transform
  houghThreshold?: number;
  houghMaxLines?: number;
  houghMergeAngleThreshold?: number;
  houghMergeDistanceThreshold?: number;
  houghSuppressionRadius?: number; // Non-maximum suppression radius in accumulator space

  // Progressive Probabilistic Hough Transform
  pphtThreshold?: number; // Minimum number of points to form a line
  pphtMinLineLength?: number; // Minimum line length in pixels
  pphtMaxLineGap?: number; // Maximum gap between points on the same line
  pphtMaxLines?: number; // Maximum number of lines to detect

  // Line filtering
  minConfidence?: number;
  minLineLength?: number;

  // Line merging
  mergeAngleThreshold?: number;
  mergeMidpointThreshold?: number;
  mergeDistanceThreshold?: number;
  mergeMaxIterations?: number; // Max iterations for iterative merging (default: 10)

  // Merge overlapping lines (lines close together with same direction, keeps outer endpoints)
  overlapAngleThreshold?: number; // Max angle difference in degrees (default: 10)
  overlapDistanceThreshold?: number; // Max perpendicular distance between lines (default: 15)
  overlapProjectionThreshold?: number; // Min overlap required along line direction (default: 0, negative allows gaps)
  overlapMaxIterations?: number; // Max iterations for iterative merging (default: 10)

  // Connect nearby endpoints (joins lines with close endpoints that have similar angle)
  connectAngleThreshold?: number; // Max angle difference in degrees (default: 10)
  connectPerpDistThreshold?: number; // Max perpendicular distance between endpoints (default: 20)
  connectLineDistThreshold?: number; // Max distance along line direction between endpoints (default: 50)
  connectMaxIterations?: number; // Max iterations for iterative connecting (default: 10)

  // Line quality verification
  qualityThreshold?: number;

  // Endpoint refinement (pixel alignment - matches PolygonProcessor)
  refinementSearchRange?: number; // Search range in pixels for perpendicular scans (default: 20)
  refinementMinProbeRatio?: number; // Minimum ratio of probes that must find a peak (default: 0.7)
  refinementMinSegmentLength?: number; // Minimum segment length in pixels (default: 50)

  // Line splitting (gap-based)
  splitMaxGap?: number; // Maximum gap in sample indices before splitting (default: 3)

  // Legacy endpoint refinement parameters (kept for backwards compatibility)
  refinementIntensityThreshold?: number;
  refinementSignificanceThreshold?: number;
  refinementSampleInterval?: number; // Distance between sample points in pixels (default: 5)

  // EDLines (Edge Drawing Lines) - fast line segment detector
  edlAnchorThreshold?: number; // Gradient magnitude threshold for anchor points (default: 36)
  edlGradientThreshold?: number; // Minimum gradient for edge pixels (default: 8)
  edlMinLineLength?: number; // Minimum line segment length in pixels (default: 15)
  edlLineFitErrorThreshold?: number; // Max perpendicular distance for line fitting (default: 1.5)
  edlScanInterval?: number; // Interval between anchor scans (default: 1)

  // Combine collinear segments - joins small segments on the same line into one
  combineAngleThreshold?: number; // Max angle difference in degrees (default: 10)
  combinePerpendicularThreshold?: number; // Max perpendicular distance from fitted line (default: 5)
  combineGapThreshold?: number; // Max gap between segments along the line direction (default: 50)

  // Ridge detection - detects centerlines of thick features (better for thick lines)
  ridgeScale?: number; // Scale for Gaussian smoothing (default: 2.0, higher = thicker lines)
  ridgeThreshold?: number; // Minimum ridge response threshold (default: 5)
  ridgeMinLineLength?: number; // Minimum line segment length in pixels (default: 15)
  ridgeLineFitErrorThreshold?: number; // Max perpendicular distance for line fitting (default: 1.5)
  ridgeScanInterval?: number; // Interval between anchor scans (default: 1)

  // Frangi vesselness filter - omnidirectional ridge/line detection (rotation-invariant)
  frangiScales?: number[]; // Array of scales (sigmas) to analyze (default: [1, 2, 3, 4])
  frangiBeta?: number; // Sensitivity to blob-like structures (default: 0.5)
  frangiC?: number; // Sensitivity to background noise, auto-calculated if not set (default: auto)
  frangiBlackRidges?: boolean; // Detect dark ridges on bright background (default: true)
  frangiThreshold?: number; // Minimum vesselness response threshold (default: 0.01)
  frangiMinLineLength?: number; // Minimum line segment length in pixels (default: 15)
  frangiLineFitErrorThreshold?: number; // Max perpendicular distance for line fitting (default: 1.5)
  frangiScanInterval?: number; // Interval between anchor scans (default: 1)

  // ELSED (Enhanced Line SEgment Drawing) - fastest line segment detector with gap jumping
  elsedGradientThreshold?: number; // Minimum gradient magnitude (default: 30)
  elsedAnchorThreshold?: number; // Gradient difference to identify anchors (default: 8)
  elsedScanInterval?: number; // Spatial sampling rate for anchor detection (default: 1)
  elsedMinLineLength?: number; // Minimum line segment length in pixels (default: 15)
  elsedLineFitErrorThreshold?: number; // Max perpendicular distance for line fitting (default: 1.5)
  elsedMaxGapSize?: number; // Maximum gap size to jump over during segment growing (default: 3)
  elsedValidationThreshold?: number; // Orientation consistency threshold (default: 0.7)
}

/**
 * Line Detection Pipeline
 *
 * Processes images through a configurable sequence of passes:
 * 1. Grayscale conversion
 * 2. Canny edge detection
 * 3. Hough line transform
 * 4. Line filtering (by confidence and length)
 * 5. Line merging (remove duplicates)
 * 6. Line quality verification
 * 7. Endpoint refinement (pixel alignment)
 */
/**
 * Distance-based parameter names that need cm to pixel conversion
 * These parameters are stored in cm but algorithms need pixel values
 */
export const LINE_DETECTION_DISTANCE_PARAMETERS = new Set([
  // Hough transform
  'houghMergeDistanceThreshold',
  'houghSuppressionRadius',
  // Progressive Probabilistic Hough Transform
  'pphtMinLineLength',
  'pphtMaxLineGap',
  // Line filtering
  'minLineLength',
  // Line merging
  'mergeMidpointThreshold',
  'mergeDistanceThreshold',
  // Merge overlapping lines
  'overlapDistanceThreshold',
  'overlapProjectionThreshold',
  // Connect nearby endpoints
  'connectPerpDistThreshold',
  'connectLineDistThreshold',
  // Endpoint refinement
  'refinementSearchRange',
  'refinementMinSegmentLength',
  // EDLines
  'edlMinLineLength',
  'edlLineFitErrorThreshold',
  // Combine collinear segments
  'combinePerpendicularThreshold',
  'combineGapThreshold',
  // Canny dilation
  'cannyDilationSize',
  // ELSED
  'elsedLineFitErrorThreshold',
  'elsedMaxGapSize',
  // Ridge detection
  'ridgeMinLineLength',
  'ridgeLineFitErrorThreshold',
  // Frangi filter
  'frangiMinLineLength',
  'frangiLineFitErrorThreshold',
]);

export class LineDetectionPipeline extends BasePipeline<
  LineDetectionInput,
  LineDetectionOutput,
  LineDetectionContext,
  LineDetectionKwargs,
  never // No array items - we process the whole image
> {
  private cmToPixelsConverter: ((cm: number) => number) | null = null;

  constructor() {
    super();
    this.initializeProcessorRegistry();
  }

  /**
   * Set the cm to pixels converter function
   * This should be called when the coordinate transformer is ready
   */
  setCmToPixels(converter: (cm: number) => number): void {
    this.cmToPixelsConverter = converter;
  }

  /**
   * Convert a single cm value to pixels using the converter
   * Returns the original value if no converter is set
   */
  private cmToPixels(cmValue: number): number {
    if (this.cmToPixelsConverter) {
      return this.cmToPixelsConverter(cmValue);
    }
    return cmValue;
  }

  /**
   * Convert distance parameters from cm to pixels in kwargs
   * Returns a new kwargs object with converted values
   */
  private convertKwargsToPixels(kwargs?: LineDetectionKwargs): LineDetectionKwargs | undefined {
    if (!kwargs || !this.cmToPixelsConverter) {
      return kwargs;
    }

    const converted: LineDetectionKwargs = { ...kwargs };
    for (const key of Object.keys(converted)) {
      if (LINE_DETECTION_DISTANCE_PARAMETERS.has(key)) {
        const cmValue = converted[key];
        if (typeof cmValue === 'number') {
          converted[key] = this.cmToPixels(cmValue);
        }
      }
    }
    return converted;
  }

  /**
   * Convert input to internal context
   */
  protected inputToContext(input: LineDetectionInput): LineDetectionContext {
    return {
      capturedImage: input.imageData,
      width: input.width,
      height: input.height,
      positionMap: input.positionMap,
      rawLines: [],
      filteredLines: [],
    };
  }

  /**
   * Convert context to output
   */
  protected contextToOutput(context: LineDetectionContext): LineDetectionOutput {
    // Create fallback ImageData for missing stages
    const fallbackImageData = new ImageData(context.width, context.height);

    return {
      lines: context.filteredLines,
      debugData: {
        capturedImage: context.capturedImage,
        grayscaleImage: context.grayscaleImage || fallbackImageData,
        edgesImage: context.edgesImage || fallbackImageData,
        rawLines: context.rawLines,
        filteredLines: context.filteredLines,
      },
    };
  }

  /**
   * Initialize processor registry with all line detection passes
   */
  private initializeProcessorRegistry(): void {
    // Pass 1: Grayscale conversion
    // When position map is available, uses B channel (height/Z value) for pixel-aligned line detection
    // This matches the pixel alignment approach in PolygonProcessor
    this.registerProcessor(
      'grayscale_conversion',
      (context: LineDetectionContext) => {
        if (context.positionMap) {
          // Use B channel from position map (height/Z value) for grayscale
          // This ensures line detection uses the same pixel alignment as PolygonProcessor
          context.grayscale = this.positionMapBToGrayscale(context.positionMap);
        } else {
          // Fallback to standard grayscale conversion from captured image
          context.grayscale = this.toGrayscale(context.capturedImage);
        }
        if (!context.grayscale) {
          throw new Error('Grayscale conversion failed: no grayscale data produced');
        }
        context.grayscaleImage = this.grayscaleToImageData(context.grayscale, context.width, context.height);
        return context;
      },
      {
        defaultKwargs: {} as LineDetectionKwargs,
      },
    );

    // Pass 2: Canny edge detection
    this.registerProcessor(
      'canny_edge_detection',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.grayscale) {
          throw new Error('Grayscale conversion must run before edge detection');
        }
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        context.edges = this.cannyEdgeDetection(context.grayscale, context.width, context.height, pxKwargs?.cannyLowThreshold ?? 80, pxKwargs?.cannyHighThreshold ?? 150);

        // Apply dilation to close gaps between parallel edges
        const dilationSize = pxKwargs?.cannyDilationSize ?? 2;
        if (dilationSize > 0 && context.edges) {
          context.edges = this.dilateEdges(context.edges, context.width, context.height, dilationSize);
        }

        context.edgesImage = context.edges ? this.grayscaleToImageData(context.edges, context.width, context.height) : new ImageData(context.width, context.height);
        return context;
      },
      {
        defaultKwargs: {
          cannyLowThreshold: 80,
          cannyHighThreshold: 150,
          cannyDilationSize: 2,
        } as LineDetectionKwargs,
      },
    );

    // Pass 3: Hough line transform
    this.registerProcessor(
      'hough_line_transform',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.edges || !context.grayscale) {
          throw new Error('Edge detection must run before Hough transform');
        }
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        context.rawLines = this.houghLineTransform(
          context.edges,
          context.grayscale,
          context.width,
          context.height,
          pxKwargs?.houghThreshold ?? 150,
          pxKwargs?.houghMaxLines,
          pxKwargs?.houghMergeAngleThreshold,
          pxKwargs?.houghMergeDistanceThreshold,
          pxKwargs?.houghSuppressionRadius,
        );
        // Also populate filteredLines with the raw lines
        context.filteredLines = [...context.rawLines];
        return context;
      },
      {
        defaultKwargs: {
          houghThreshold: 150,
          houghMaxLines: 100,
          houghMergeAngleThreshold: 25,
          houghMergeDistanceThreshold: 50,
          houghSuppressionRadius: 10,
        } as LineDetectionKwargs,
      },
    );

    // Pass 4: Filter lines by confidence and length
    this.registerProcessor(
      'filter_lines',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        const minConfidence = pxKwargs?.minConfidence ?? 0.7;
        const minLineLength = pxKwargs?.minLineLength ?? 100;

        context.filteredLines = context.rawLines.filter((line) => {
          // Check confidence
          const confidence = Math.min(1.0, line.votes / 100);
          if (confidence < minConfidence) return false;

          // Check length
          const dx = line.x2 - line.x1;
          const dy = line.y2 - line.y1;
          const length = Math.sqrt(dx * dx + dy * dy);
          return length >= minLineLength;
        });

        return context;
      },
      {
        defaultKwargs: {
          minConfidence: 0.7,
          minLineLength: 100,
        } as LineDetectionKwargs,
      },
    );

    // Pass 5: Merge similar lines in Cartesian space
    this.registerProcessor(
      'merge_cartesian_lines',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        context.filteredLines = this.mergeCartesianLines(
          context.filteredLines,
          context.width,
          context.height,
          pxKwargs?.mergeAngleThreshold ?? 15,
          pxKwargs?.mergeMidpointThreshold ?? 70,
          pxKwargs?.mergeDistanceThreshold ?? 42,
          pxKwargs?.mergeMaxIterations ?? 10,
        );
        return context;
      },
      {
        defaultKwargs: {
          mergeAngleThreshold: 15,
          mergeMidpointThreshold: 70,
          mergeDistanceThreshold: 42,
          mergeMaxIterations: 10,
        } as LineDetectionKwargs,
      },
    );

    // Pass 5b: Merge overlapping lines (close together, same direction) keeping outer endpoints
    this.registerProcessor(
      'merge_overlapping_lines',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        context.filteredLines = this.mergeOverlappingLines(
          context.filteredLines,
          pxKwargs?.overlapAngleThreshold ?? 10,
          pxKwargs?.overlapDistanceThreshold ?? 15,
          pxKwargs?.overlapProjectionThreshold ?? 0,
          pxKwargs?.overlapMaxIterations ?? 10,
        );
        return context;
      },
      {
        defaultKwargs: {
          overlapAngleThreshold: 10,
          overlapDistanceThreshold: 15,
          overlapProjectionThreshold: 0,
          overlapMaxIterations: 10,
        } as LineDetectionKwargs,
      },
    );

    // Pass 5c: Connect nearby endpoints (joins lines with close endpoints and similar angle)
    this.registerProcessor(
      'connect_nearby_endpoints',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        context.filteredLines = this.connectNearbyEndpoints(
          context.filteredLines,
          pxKwargs?.connectAngleThreshold ?? 10,
          pxKwargs?.connectPerpDistThreshold ?? 20,
          pxKwargs?.connectLineDistThreshold ?? 50,
          pxKwargs?.connectMaxIterations ?? 10,
        );
        return context;
      },
      {
        defaultKwargs: {
          connectAngleThreshold: 10,
          connectPerpDistThreshold: 20,
          connectLineDistThreshold: 50,
          connectMaxIterations: 10,
        } as LineDetectionKwargs,
      },
    );

    // Pass 6: Combine collinear segments into single lines
    this.registerProcessor(
      'combine_collinear_segments',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        context.filteredLines = this.combineCollinearSegments(context.filteredLines, pxKwargs?.combineAngleThreshold ?? 10, pxKwargs?.combinePerpendicularThreshold ?? 5, pxKwargs?.combineGapThreshold ?? 50);
        return context;
      },
      {
        defaultKwargs: {
          combineAngleThreshold: 10,
          combinePerpendicularThreshold: 5,
          combineGapThreshold: 50,
        } as LineDetectionKwargs,
      },
    );

    // Pass 7: Verify line quality
    this.registerProcessor(
      'verify_line_quality',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.edges) {
          throw new Error('Edge detection must run before quality verification');
        }
        const edges = context.edges;
        const qualityThreshold = kwargs?.qualityThreshold ?? 0.3;

        context.filteredLines = context.filteredLines.filter((line) => {
          const quality = this.verifyLineQuality(edges, context.width, context.height, line.x1, line.y1, line.x2, line.y2);
          return quality > qualityThreshold;
        });

        return context;
      },
      {
        defaultKwargs: {
          qualityThreshold: 0.3,
        } as LineDetectionKwargs,
      },
    );

    // Pass 7: Refine line endpoints using pixel alignment (identical to PolygonProcessor)
    // Uses captured image (density mask) for pixel alignment with density peak detection
    this.registerProcessor(
      'refine_endpoints',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);
        const searchRange = pxKwargs?.refinementSearchRange ?? 20;
        const minProbeRatio = pxKwargs?.refinementMinProbeRatio ?? 0.7;

        // Refine pass: Use pixel alignment identical to PolygonProcessor
        // Uses density mask (captured image) to find peaks, optionally with position map for 12-bit XY
        const refinedLines: Array<{
          line: { x1: number; y1: number; x2: number; y2: number; votes: number };
          peakData: Array<{ x: number; y: number; index: number }>;
        }> = [];

        for (const line of context.filteredLines) {
          const refined = this.refineLinePixelAlignment(
            line,
            context.capturedImage, // density mask
            context.positionMap, // optional position map for 12-bit XY decoding
            searchRange,
            minProbeRatio,
          );
          refinedLines.push(refined);
        }

        context.filteredLines = refinedLines.map((r) => r.line);
        return context;
      },
      {
        defaultKwargs: {
          refinementSearchRange: 20,
          refinementMinProbeRatio: 0.7,
        } as LineDetectionKwargs,
      },
    );

    // Pass 8: Split lines by gaps in peak coverage
    // Separate pass for splitting lines into segments based on gaps in detected peaks
    this.registerProcessor(
      'split_lines',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);
        const searchRange = pxKwargs?.refinementSearchRange ?? 20;
        const minProbeRatio = pxKwargs?.refinementMinProbeRatio ?? 0.7;
        const minSegmentLength = pxKwargs?.refinementMinSegmentLength ?? 50;
        const maxGap = pxKwargs?.splitMaxGap ?? 3;

        // First refine to get peak data, then split based on gaps
        const resultLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];

        for (const line of context.filteredLines) {
          // Get peak data from refinement
          const refined = this.refineLinePixelAlignment(line, context.capturedImage, context.positionMap, searchRange, minProbeRatio);

          // Split based on gaps in peak coverage
          const segments = this.splitLineByGaps(refined.line, refined.peakData, minSegmentLength, maxGap);
          resultLines.push(...segments);
        }

        context.filteredLines = resultLines;
        return context;
      },
      {
        defaultKwargs: {
          refinementSearchRange: 20,
          refinementMinProbeRatio: 0.7,
          refinementMinSegmentLength: 50,
          splitMaxGap: 3,
        } as LineDetectionKwargs,
      },
    );

    // Alternative: Progressive Probabilistic Hough Transform
    this.registerProcessor(
      'progressive_probabilistic_hough_transform',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.edges || !context.grayscale) {
          throw new Error('Edge detection must run before PPHT');
        }
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        context.rawLines = this.progressiveProbabilisticHoughTransform(
          context.edges,
          context.width,
          context.height,
          pxKwargs?.pphtThreshold ?? 50,
          pxKwargs?.pphtMinLineLength ?? 50,
          pxKwargs?.pphtMaxLineGap ?? 10,
          pxKwargs?.pphtMaxLines ?? 100,
        );
        // Also populate filteredLines with the raw lines
        context.filteredLines = [...context.rawLines];
        return context;
      },
      {
        defaultKwargs: {
          pphtThreshold: 50,
          pphtMinLineLength: 50,
          pphtMaxLineGap: 10,
          pphtMaxLines: 100,
        } as LineDetectionKwargs,
      },
    );

    // EDLines: Edge Drawing Lines - Fast line segment detector
    // 10-11x faster than LSD with excellent false detection control
    this.registerProcessor(
      'edlines',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.grayscale) {
          throw new Error('Grayscale conversion must run before EDLines');
        }
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        const result = this.edLines(
          context.grayscale,
          context.width,
          context.height,
          pxKwargs?.edlAnchorThreshold ?? 36,
          pxKwargs?.edlGradientThreshold ?? 8,
          pxKwargs?.edlMinLineLength ?? 15,
          pxKwargs?.edlLineFitErrorThreshold ?? 1.5,
          pxKwargs?.edlScanInterval ?? 1,
        );

        context.rawLines = result.lines;
        context.filteredLines = [...result.lines];

        // Store edge image for visualization
        if (result.edgeImage) {
          context.edges = result.edgeImage;
          context.edgesImage = this.grayscaleToImageData(result.edgeImage, context.width, context.height);
        }

        return context;
      },
      {
        defaultKwargs: {
          edlAnchorThreshold: 36,
          edlGradientThreshold: 8,
          edlMinLineLength: 15,
          edlLineFitErrorThreshold: 1.5,
          edlScanInterval: 1,
        } as LineDetectionKwargs,
      },
    );

    // Ridge Detection - finds centerlines of thick features
    // Better than edge detection for thick lines as it finds the center rather than both edges
    this.registerProcessor(
      'ridge_detection',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.grayscale) {
          throw new Error('Grayscale conversion must run before ridge detection');
        }
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        const result = this.ridgeDetection(
          context.grayscale,
          context.width,
          context.height,
          pxKwargs?.ridgeScale ?? 2.0,
          pxKwargs?.ridgeThreshold ?? 5,
          pxKwargs?.ridgeMinLineLength ?? 15,
          pxKwargs?.ridgeLineFitErrorThreshold ?? 1.5,
          pxKwargs?.ridgeScanInterval ?? 1,
        );

        context.rawLines = result.lines;
        context.filteredLines = [...result.lines];

        // Store ridge image for visualization
        if (result.ridgeImage) {
          context.edges = result.ridgeImage;
          context.edgesImage = this.grayscaleToImageData(result.ridgeImage, context.width, context.height);
        }

        return context;
      },
      {
        defaultKwargs: {
          ridgeScale: 2.0,
          ridgeThreshold: 5,
          ridgeMinLineLength: 15,
          ridgeLineFitErrorThreshold: 1.5,
          ridgeScanInterval: 1,
        } as LineDetectionKwargs,
      },
    );

    // Frangi Vesselness Filter - omnidirectional ridge/line detection
    // Rotation-invariant, multi-scale analysis using eigenvalues of Hessian matrix
    // Best for detecting lines/ridges in any orientation
    this.registerProcessor(
      'frangi_filter',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.grayscale) {
          throw new Error('Grayscale conversion must run before Frangi filter');
        }
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        const result = this.frangiFilter(
          context.grayscale,
          context.width,
          context.height,
          pxKwargs?.frangiScales ?? [1, 2, 3, 4],
          pxKwargs?.frangiBeta ?? 0.5,
          pxKwargs?.frangiC,
          pxKwargs?.frangiBlackRidges ?? true,
          pxKwargs?.frangiThreshold ?? 0.01,
          pxKwargs?.frangiMinLineLength ?? 15,
          pxKwargs?.frangiLineFitErrorThreshold ?? 1.5,
          pxKwargs?.frangiScanInterval ?? 1,
        );

        context.rawLines = result.lines;
        context.filteredLines = [...result.lines];

        // Store vesselness image for visualization
        if (result.vesselnessImage) {
          context.edges = result.vesselnessImage;
          context.edgesImage = this.grayscaleToImageData(result.vesselnessImage, context.width, context.height);
        }

        return context;
      },
      {
        defaultKwargs: {
          frangiScales: [1, 2, 3, 4],
          frangiBeta: 0.5,
          frangiBlackRidges: true,
          frangiThreshold: 0.01,
          frangiMinLineLength: 15,
          frangiLineFitErrorThreshold: 1.5,
          frangiScanInterval: 1,
        } as LineDetectionKwargs,
      },
    );

    // ELSED: Enhanced Line SEgment Drawing - fastest line segment detector
    // Key improvement over EDLines: can jump over small gaps/discontinuities during segment growing
    this.registerProcessor(
      'elsed',
      (context: LineDetectionContext, kwargs?: LineDetectionKwargs) => {
        if (!context.grayscale) {
          throw new Error('Grayscale conversion must run before ELSED');
        }
        // Convert distance parameters from cm to pixels
        const pxKwargs = this.convertKwargsToPixels(kwargs);

        const result = this.elsed(
          context.grayscale,
          context.width,
          context.height,
          pxKwargs?.elsedGradientThreshold ?? 30,
          pxKwargs?.elsedAnchorThreshold ?? 8,
          pxKwargs?.elsedScanInterval ?? 1,
          pxKwargs?.elsedMinLineLength ?? 15,
          pxKwargs?.elsedLineFitErrorThreshold ?? 1.5,
          pxKwargs?.elsedMaxGapSize ?? 3,
          pxKwargs?.elsedValidationThreshold ?? 0.7,
        );

        context.rawLines = result.lines;
        context.filteredLines = [...result.lines];

        // Store edge image for visualization
        if (result.edgeImage) {
          context.edges = result.edgeImage;
          context.edgesImage = this.grayscaleToImageData(result.edgeImage, context.width, context.height);
        }

        return context;
      },
      {
        defaultKwargs: {
          elsedGradientThreshold: 30,
          elsedAnchorThreshold: 8,
          elsedScanInterval: 1,
          elsedMinLineLength: 15,
          elsedLineFitErrorThreshold: 1.5,
          elsedMaxGapSize: 3,
          elsedValidationThreshold: 0.7,
        } as LineDetectionKwargs,
      },
    );
  }

  // ========== Image Processing Helper Methods ==========

  /**
   * Convert image to grayscale
   */
  private toGrayscale(imageData: ImageData): Uint8ClampedArray {
    const data = imageData.data;
    const grayscale = new Uint8ClampedArray(data.length / 4);

    for (let i = 0; i < data.length; i += 4) {
      const r = data[i];
      const g = data[i + 1];
      const b = data[i + 2];
      grayscale[i / 4] = Math.round(0.299 * r + 0.587 * g + 0.114 * b);
    }

    return grayscale;
  }

  /**
   * Extract B channel from position map as grayscale.
   * The position map encodes: R=X high, G=Y high, B=Z (height), A=XY low bits.
   * Using the B channel (eye-space depth) gives pixel-aligned line detection
   * that matches the PolygonProcessor's pixel alignment approach.
   *
   * Filters out the closest-to-camera 5% of B values, which for a top-down
   * floor capture removes ceiling pixels (ceiling sits at the near plane
   * with the new eye-space encoding). The bottom 5% by value is removed
   * because that's where ceiling/near-camera artifacts cluster.
   */
  private positionMapBToGrayscale(positionMap: ImageData): Uint8ClampedArray {
    const data = positionMap.data;
    const pixelCount = data.length / 4;
    const grayscale = new Uint8ClampedArray(pixelCount);

    // First pass: collect all non-zero B values to compute the 5th percentile threshold
    const bValues: number[] = [];
    for (let i = 0; i < data.length; i += 4) {
      const b = data[i + 2];
      if (b > 0) {
        bValues.push(b);
      }
    }

    // Calculate 5th percentile threshold (filter out bottom 5% — closest to
    // camera, typically ceiling for top-down or in-front-of-wall for wall views).
    let threshold = 0;
    if (bValues.length > 0) {
      bValues.sort((a, b) => a - b);
      const percentileIndex = Math.floor(bValues.length * 0.05);
      threshold = bValues[Math.min(percentileIndex, bValues.length - 1)];
    }

    // Second pass: extract B channel, filtering out values below the threshold
    for (let i = 0; i < data.length; i += 4) {
      const b = data[i + 2];
      // Set to black if below threshold (close-to-camera) or if zero (no data)
      grayscale[i / 4] = b < threshold ? 0 : b;
    }

    return grayscale;
  }

  /**
   * Convert grayscale array to ImageData for visualization
   */
  private grayscaleToImageData(grayscale: Uint8ClampedArray, width: number, height: number): ImageData {
    const imageData = new ImageData(width, height);
    for (let i = 0; i < grayscale.length; i++) {
      const value = grayscale[i];
      imageData.data[i * 4] = value;
      imageData.data[i * 4 + 1] = value;
      imageData.data[i * 4 + 2] = value;
      imageData.data[i * 4 + 3] = 255;
    }
    return imageData;
  }

  /**
   * Dilate edges to close gaps between parallel edges
   * Uses morphological dilation with a square kernel
   */
  private dilateEdges(edges: Uint8ClampedArray, width: number, height: number, kernelSize: number): Uint8ClampedArray {
    const dilated = new Uint8ClampedArray(edges.length);
    const radius = Math.floor(kernelSize / 2);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let maxValue = 0;

        // Check neighborhood
        for (let ky = -radius; ky <= radius; ky++) {
          for (let kx = -radius; kx <= radius; kx++) {
            const nx = x + kx;
            const ny = y + ky;

            if (nx >= 0 && nx < width && ny >= 0 && ny < height) {
              const value = edges[ny * width + nx];
              if (value > maxValue) {
                maxValue = value;
              }
            }
          }
        }

        dilated[y * width + x] = maxValue;
      }
    }

    return dilated;
  }

  /**
   * Simple Canny edge detection
   */
  private cannyEdgeDetection(grayscale: Uint8ClampedArray, width: number, height: number, lowThreshold: number, highThreshold: number): Uint8ClampedArray {
    const edges = new Uint8ClampedArray(grayscale.length);

    const sobelX = [-1, 0, 1, -2, 0, 2, -1, 0, 1];
    const sobelY = [-1, -2, -1, 0, 0, 0, 1, 2, 1];

    for (let y = 1; y < height - 1; y++) {
      for (let x = 1; x < width - 1; x++) {
        let gx = 0;
        let gy = 0;

        for (let ky = -1; ky <= 1; ky++) {
          for (let kx = -1; kx <= 1; kx++) {
            const idx = (y + ky) * width + (x + kx);
            const kernelIdx = (ky + 1) * 3 + (kx + 1);
            gx += grayscale[idx] * sobelX[kernelIdx];
            gy += grayscale[idx] * sobelY[kernelIdx];
          }
        }

        const magnitude = Math.sqrt(gx * gx + gy * gy);
        const idx = y * width + x;

        if (magnitude > highThreshold) {
          edges[idx] = 255;
        } else if (magnitude > lowThreshold) {
          edges[idx] = 128;
        } else {
          edges[idx] = 0;
        }
      }
    }

    return edges;
  }

  /**
   * Hough Line Transform using polar coordinates
   */
  private houghLineTransform(
    edges: Uint8ClampedArray,
    grayscale: Uint8ClampedArray,
    width: number,
    height: number,
    threshold: number,
    maxLines?: number,
    mergeAngleThreshold?: number,
    mergeDistanceThreshold?: number,
    suppressionRadius?: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];

    const thetaStep = Math.PI / 180;
    const numTheta = 180;
    const diagonal = Math.sqrt(width * width + height * height);
    const rhoStep = 1;
    const numRho = Math.ceil(diagonal * 2);

    const accumulator = new Uint32Array(numTheta * numRho);

    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        if (edges[y * width + x] > 0) {
          for (let thetaIdx = 0; thetaIdx < numTheta; thetaIdx++) {
            const theta = thetaIdx * thetaStep;
            const rho = x * Math.cos(theta) + y * Math.sin(theta);
            const rhoIdx = Math.floor((rho + diagonal) / rhoStep);

            if (rhoIdx >= 0 && rhoIdx < numRho) {
              accumulator[thetaIdx * numRho + rhoIdx]++;
            }
          }
        }
      }
    }

    // Apply non-maximum suppression to find local peaks
    const peaks: Array<{ theta: number; rho: number; votes: number }> = [];
    const suppRadius = suppressionRadius ?? 10; // Radius for non-maximum suppression

    // Use adaptive suppression based on image size
    const thetaSuppression = Math.max(3, Math.floor(suppRadius / 2)); // Smaller in angle dimension
    const rhoSuppression = suppRadius; // Larger in distance dimension

    for (let thetaIdx = 0; thetaIdx < numTheta; thetaIdx++) {
      for (let rhoIdx = 0; rhoIdx < numRho; rhoIdx++) {
        const votes = accumulator[thetaIdx * numRho + rhoIdx];

        if (votes >= threshold) {
          // Check if this is a local maximum
          let isLocalMax = true;
          for (let dt = -thetaSuppression; dt <= thetaSuppression && isLocalMax; dt++) {
            for (let dr = -rhoSuppression; dr <= rhoSuppression && isLocalMax; dr++) {
              if (dt === 0 && dr === 0) continue;

              let checkTheta = thetaIdx + dt;
              const checkRho = rhoIdx + dr;

              // Handle theta wrapping (0° and 180° are the same line)
              if (checkTheta < 0) checkTheta += numTheta;
              if (checkTheta >= numTheta) checkTheta -= numTheta;

              if (checkRho >= 0 && checkRho < numRho) {
                const neighborVotes = accumulator[checkTheta * numRho + checkRho];
                if (neighborVotes > votes) {
                  isLocalMax = false;
                }
              }
            }
          }

          if (isLocalMax) {
            const theta = thetaIdx * thetaStep;
            const rho = rhoIdx * rhoStep - diagonal;
            peaks.push({ theta, rho, votes });
          }
        }
      }
    }

    peaks.sort((a, b) => b.votes - a.votes);
    const mergedPeaks: Array<{ theta: number; rho: number; votes: number }> = [];

    const maxLinesLimit = maxLines ?? 100;
    const angleDiffThreshold = (mergeAngleThreshold ?? 25) * (Math.PI / 180);
    const distanceThreshold = mergeDistanceThreshold ?? 50;

    for (const peak of peaks) {
      let merged = false;
      for (const existing of mergedPeaks) {
        const thetaDiff = Math.abs(peak.theta - existing.theta);
        const rhoDiff = Math.abs(peak.rho - existing.rho);

        if (thetaDiff < angleDiffThreshold && rhoDiff < distanceThreshold) {
          if (peak.votes > existing.votes) {
            existing.theta = peak.theta;
            existing.rho = peak.rho;
            existing.votes = peak.votes;
          }
          merged = true;
          break;
        }
      }

      if (!merged && mergedPeaks.length < maxLinesLimit) {
        mergedPeaks.push({ ...peak });
      }
    }

    for (const peak of mergedPeaks) {
      const { theta, rho, votes } = peak;
      const cos = Math.cos(theta);
      const sin = Math.sin(theta);

      // Convert polar line (rho, theta) to two endpoint coordinates
      // Line equation: x * cos(theta) + y * sin(theta) = rho
      // Find intersection points with image boundaries

      const intersections: Array<{ x: number; y: number }> = [];

      // Check intersection with top edge (y = 0)
      if (Math.abs(cos) > 0.001) {
        const x = rho / cos;
        if (x >= 0 && x < width) {
          intersections.push({ x, y: 0 });
        }
      }

      // Check intersection with bottom edge (y = height - 1)
      if (Math.abs(cos) > 0.001) {
        const x = (rho - (height - 1) * sin) / cos;
        if (x >= 0 && x < width) {
          intersections.push({ x, y: height - 1 });
        }
      }

      // Check intersection with left edge (x = 0)
      if (Math.abs(sin) > 0.001) {
        const y = rho / sin;
        if (y >= 0 && y < height) {
          intersections.push({ x: 0, y });
        }
      }

      // Check intersection with right edge (x = width - 1)
      if (Math.abs(sin) > 0.001) {
        const y = (rho - (width - 1) * cos) / sin;
        if (y >= 0 && y < height) {
          intersections.push({ x: width - 1, y });
        }
      }

      // We should have exactly 2 intersections for a line crossing the image
      if (intersections.length >= 2) {
        // Remove duplicate intersections (can happen at corners)
        const uniqueIntersections = intersections.filter((point, index) => {
          for (let i = 0; i < index; i++) {
            if (Math.abs(point.x - intersections[i].x) < 1 && Math.abs(point.y - intersections[i].y) < 1) {
              return false;
            }
          }
          return true;
        });

        if (uniqueIntersections.length >= 2) {
          lines.push({
            x1: uniqueIntersections[0].x,
            y1: uniqueIntersections[0].y,
            x2: uniqueIntersections[1].x,
            y2: uniqueIntersections[1].y,
            votes,
          });
        }
      }
    }

    return lines;
  }

  /**
   * Progressive Probabilistic Hough Transform
   *
   * Simplified fast implementation - uses flood fill to find connected edge components,
   * then fits lines to them. Much faster than pixel-by-pixel growing.
   */
  private progressiveProbabilisticHoughTransform(
    edges: Uint8ClampedArray,
    width: number,
    height: number,
    threshold: number,
    minLineLength: number,
    maxLineGap: number,
    maxLines: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const used = new Uint8Array(width * height);

    // 8-connected neighbors
    const neighbors = [
      [-1, -1],
      [0, -1],
      [1, -1],
      [-1, 0],
      [1, 0],
      [-1, 1],
      [0, 1],
      [1, 1],
    ];

    // Process all edge pixels
    for (let y = 0; y < height && lines.length < maxLines; y++) {
      for (let x = 0; x < width && lines.length < maxLines; x++) {
        if (!edges[y * width + x]) continue;
        if (used[y * width + x]) continue;

        // Flood fill to find connected component
        const segment: Array<{ x: number; y: number }> = [];
        const queue: Array<{ x: number; y: number }> = [{ x, y }];
        used[y * width + x] = 1;

        while (queue.length > 0) {
          const current = queue.shift();
          if (!current) break;
          segment.push(current);

          // Check 8-connected neighbors
          for (const [dx, dy] of neighbors) {
            const nx = current.x + dx;
            const ny = current.y + dy;

            if (nx < 0 || nx >= width || ny < 0 || ny >= height) continue;
            if (used[ny * width + nx]) continue;
            if (!edges[ny * width + nx]) continue;

            used[ny * width + nx] = 1;
            queue.push({ x: nx, y: ny });
          }
        }

        // Filter short segments
        if (segment.length < threshold) continue;

        // Fit line to segment using least squares
        const fitResult = this.fitLineLeastSquares(segment);
        if (!fitResult) continue;

        const { point, direction } = fitResult;

        // Project all points onto the fitted line to find endpoints
        const projections = segment.map((p) => {
          const dx = p.x - point.x;
          const dy = p.y - point.y;
          return dx * direction.x + dy * direction.y;
        });

        const minProj = Math.min(...projections);
        const maxProj = Math.max(...projections);

        const x1 = point.x + direction.x * minProj;
        const y1 = point.y + direction.y * minProj;
        const x2 = point.x + direction.x * maxProj;
        const y2 = point.y + direction.y * maxProj;

        const length = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);

        if (length >= minLineLength) {
          lines.push({
            x1: Math.round(x1),
            y1: Math.round(y1),
            x2: Math.round(x2),
            y2: Math.round(y2),
            votes: segment.length,
          });
        }
      }
    }

    return lines;
  }

  /**
   * EDLines: Edge Drawing Lines - Fast line segment detector
   *
   * Optimized implementation based on the EDLines algorithm.
   * Key optimizations:
   * - Avoids trig functions by storing gx/gy directly
   * - Uses typed arrays for chain storage (flat x,y pairs)
   * - Minimizes object allocations
   *
   * Reference: "EDLines: A real-time line segment detector with a false detection control"
   */
  private edLines(
    grayscale: Uint8ClampedArray,
    width: number,
    height: number,
    anchorThreshold: number,
    gradientThreshold: number,
    minLineLength: number,
    lineFitErrorThreshold: number,
    scanInterval: number,
  ): {
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
    edgeImage: Uint8ClampedArray;
  } {
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const size = width * height;

    // Step 1: Compute gradient using Sobel - store gx, gy directly (avoid atan2)
    const gradientMag = new Uint16Array(size); // Use integer for faster comparison
    const gradientGx = new Int16Array(size);
    const gradientGy = new Int16Array(size);

    const anchorThresholdSq = anchorThreshold * anchorThreshold;
    const gradientThresholdSq = gradientThreshold * gradientThreshold;

    for (let y = 1; y < height - 1; y++) {
      const rowOffset = y * width;
      const rowAbove = (y - 1) * width;
      const rowBelow = (y + 1) * width;

      for (let x = 1; x < width - 1; x++) {
        // Sobel kernels - unrolled for speed
        const tl = grayscale[rowAbove + x - 1];
        const tc = grayscale[rowAbove + x];
        const tr = grayscale[rowAbove + x + 1];
        const ml = grayscale[rowOffset + x - 1];
        const mr = grayscale[rowOffset + x + 1];
        const bl = grayscale[rowBelow + x - 1];
        const bc = grayscale[rowBelow + x];
        const br = grayscale[rowBelow + x + 1];

        const gx = -tl + tr - 2 * ml + 2 * mr - bl + br;
        const gy = -tl - 2 * tc - tr + bl + 2 * bc + br;

        const idx = rowOffset + x;
        const magSq = gx * gx + gy * gy;
        gradientMag[idx] = magSq > 65535 ? 65535 : magSq; // Clamp to uint16
        gradientGx[idx] = gx;
        gradientGy[idx] = gy;
      }
    }

    // Step 2: Find anchor points using pre-allocated array
    const maxAnchors = Math.ceil((width * height) / (scanInterval * scanInterval * 4));
    const anchorX = new Uint16Array(maxAnchors);
    const anchorY = new Uint16Array(maxAnchors);
    const anchorMag = new Uint32Array(maxAnchors);
    let anchorCount = 0;

    for (let y = 2; y < height - 2; y += scanInterval) {
      const rowOffset = y * width;

      for (let x = 2; x < width - 2; x += scanInterval) {
        const idx = rowOffset + x;
        const magSq = gradientMag[idx];

        if (magSq < anchorThresholdSq) continue;

        const gx = gradientGx[idx];
        const gy = gradientGy[idx];

        // Determine if gradient is more horizontal or vertical using gx/gy directly
        const absGx = gx < 0 ? -gx : gx;
        const absGy = gy < 0 ? -gy : gy;
        const isHorizontal = absGx > absGy;

        let isAnchor: boolean;
        if (isHorizontal) {
          // Compare with vertical neighbors
          isAnchor = magSq > gradientMag[(y - 1) * width + x] && magSq > gradientMag[(y + 1) * width + x];
        } else {
          // Compare with horizontal neighbors
          isAnchor = magSq > gradientMag[rowOffset + x - 1] && magSq > gradientMag[rowOffset + x + 1];
        }

        if (isAnchor && anchorCount < maxAnchors) {
          anchorX[anchorCount] = x;
          anchorY[anchorCount] = y;
          anchorMag[anchorCount] = magSq;
          anchorCount++;
        }
      }
    }

    // Sort anchors by magnitude (descending) using O(n log n) sort
    this.sortAnchorsByMagnitude(anchorX, anchorY, anchorMag, anchorCount);

    // Step 3: Draw edge chains from anchors
    const edgeImage = new Uint8ClampedArray(size);
    const visited = new Uint8Array(size);

    // Pre-allocate chain storage (flat array: x0, y0, x1, y1, ...)
    const maxChainSize = 2000;
    const chainBuffer = new Int16Array(maxChainSize * 2);

    for (let a = 0; a < anchorCount; a++) {
      const startX = anchorX[a];
      const startY = anchorY[a];
      const startIdx = startY * width + startX;

      if (visited[startIdx]) continue;
      visited[startIdx] = 1;

      // Trace in both directions, storing results in chainBuffer
      let chainLen = 0;

      // Trace backward (will be reversed)
      const backwardLen = this.traceEdgeChainFast(startX, startY, -1, gradientGx, gradientGy, gradientMag, gradientThresholdSq, visited, width, height, chainBuffer, 0, maxChainSize);

      // Reverse backward chain into final position
      for (let i = 0; i < backwardLen; i++) {
        const srcIdx = (backwardLen - 1 - i) * 2;
        chainBuffer[chainLen * 2] = chainBuffer[srcIdx];
        chainBuffer[chainLen * 2 + 1] = chainBuffer[srcIdx + 1];
        chainLen++;
      }

      // Add anchor point
      chainBuffer[chainLen * 2] = startX;
      chainBuffer[chainLen * 2 + 1] = startY;
      chainLen++;

      // Trace forward
      const forwardLen = this.traceEdgeChainFast(startX, startY, 1, gradientGx, gradientGy, gradientMag, gradientThresholdSq, visited, width, height, chainBuffer, chainLen, maxChainSize);
      chainLen += forwardLen;

      // Mark edge pixels
      for (let i = 0; i < chainLen; i++) {
        const px = chainBuffer[i * 2];
        const py = chainBuffer[i * 2 + 1];
        edgeImage[py * width + px] = 255;
      }

      // Fit line segments
      if (chainLen >= minLineLength) {
        this.fitLineSegmentsFast(chainBuffer, chainLen, lineFitErrorThreshold, minLineLength, lines);
      }
    }

    return { lines, edgeImage };
  }

  /**
   * Ridge Detection - finds centerlines of thick features using Hessian matrix
   *
   * Unlike edge detection which finds boundaries (both sides of thick lines),
   * ridge detection finds the center of elongated bright/dark structures.
   * This is ideal for thick lines where edge detection would produce two parallel edges.
   *
   * Uses eigenvalue analysis of the Hessian matrix to identify ridge points,
   * then traces and fits line segments similar to EDLines.
   */
  private ridgeDetection(
    grayscale: Uint8ClampedArray,
    width: number,
    height: number,
    scale: number,
    ridgeThreshold: number,
    minLineLength: number,
    lineFitErrorThreshold: number,
    scanInterval: number,
  ): {
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
    ridgeImage: Uint8ClampedArray;
  } {
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const size = width * height;

    // Step 1: Apply Gaussian smoothing to reduce noise
    const smoothed = this.gaussianSmooth(grayscale, width, height, scale);

    // Step 2: Compute Hessian matrix components (second derivatives)
    const hessianXX = new Float32Array(size);
    const hessianXY = new Float32Array(size);
    const hessianYY = new Float32Array(size);

    for (let y = 1; y < height - 1; y++) {
      const rowOffset = y * width;
      for (let x = 1; x < width - 1; x++) {
        const idx = rowOffset + x;

        // Second derivative in X (Ixx)
        hessianXX[idx] = smoothed[idx - 1] - 2 * smoothed[idx] + smoothed[idx + 1];

        // Second derivative in Y (Iyy)
        hessianYY[idx] = smoothed[idx - width] - 2 * smoothed[idx] + smoothed[idx + width];

        // Mixed derivative (Ixy)
        hessianXY[idx] = (smoothed[idx - width - 1] - smoothed[idx - width + 1] - smoothed[idx + width - 1] + smoothed[idx + width + 1]) / 4;
      }
    }

    // Step 3: Compute ridge response using eigenvalue analysis
    // For a ridge, we want one large eigenvalue (across the ridge) and one small (along the ridge)
    const ridgeResponse = new Float32Array(size);
    const ridgeDirection = new Float32Array(size); // Direction along the ridge (principal eigenvector)

    for (let y = 2; y < height - 2; y++) {
      const rowOffset = y * width;
      for (let x = 2; x < width - 2; x++) {
        const idx = rowOffset + x;

        const xx = hessianXX[idx];
        const xy = hessianXY[idx];
        const yy = hessianYY[idx];

        // Eigenvalues of 2x2 Hessian matrix using quadratic formula
        // λ = (trace ± sqrt(trace² - 4*det)) / 2
        const trace = xx + yy;
        const det = xx * yy - xy * xy;
        const discriminant = trace * trace - 4 * det;

        if (discriminant < 0) continue;

        const sqrtDisc = Math.sqrt(discriminant);
        const lambda1 = (trace + sqrtDisc) / 2; // Larger eigenvalue
        const lambda2 = (trace - sqrtDisc) / 2; // Smaller eigenvalue

        // Ridge detection: we want a strong negative eigenvalue (dark ridge on bright background)
        // or strong positive eigenvalue (bright ridge on dark background)
        // Use the absolute value of the more extreme eigenvalue
        const absLambda1 = Math.abs(lambda1);
        const absLambda2 = Math.abs(lambda2);

        // The ridge response is the magnitude of the eigenvalue with larger absolute value
        const response = absLambda1 > absLambda2 ? absLambda1 : absLambda2;

        // Only keep strong ridges
        if (response < ridgeThreshold) continue;

        // Compute the eigenvector corresponding to the smaller eigenvalue (ridge direction)
        // This is the direction along the ridge
        let dirX: number, dirY: number;
        if (Math.abs(xy) > 1e-6) {
          // Eigenvector for smaller eigenvalue
          const smallerLambda = absLambda1 > absLambda2 ? lambda2 : lambda1;
          dirX = smallerLambda - yy;
          dirY = xy;
        } else {
          // Hessian is diagonal
          if (Math.abs(xx) < Math.abs(yy)) {
            dirX = 1;
            dirY = 0;
          } else {
            dirX = 0;
            dirY = 1;
          }
        }

        // Normalize direction
        const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
        if (dirLen > 1e-6) {
          ridgeDirection[idx] = Math.atan2(dirY / dirLen, dirX / dirLen);
        }

        ridgeResponse[idx] = response;
      }
    }

    // Step 4: Non-maximum suppression perpendicular to ridge direction
    const ridgeImage = new Uint8ClampedArray(size);
    const ridgeMagnitude = new Uint16Array(size);

    for (let y = 2; y < height - 2; y++) {
      const rowOffset = y * width;
      for (let x = 2; x < width - 2; x++) {
        const idx = rowOffset + x;
        const response = ridgeResponse[idx];

        if (response < ridgeThreshold) continue;

        // Direction perpendicular to ridge (across the ridge)
        const ridgeDir = ridgeDirection[idx];
        const perpX = -Math.sin(ridgeDir);
        const perpY = Math.cos(ridgeDir);

        // Check neighbors in perpendicular direction
        const nx1 = Math.round(x + perpX);
        const ny1 = Math.round(y + perpY);
        const nx2 = Math.round(x - perpX);
        const ny2 = Math.round(y - perpY);

        const neighbor1 = nx1 >= 0 && nx1 < width && ny1 >= 0 && ny1 < height ? ridgeResponse[ny1 * width + nx1] : 0;
        const neighbor2 = nx2 >= 0 && nx2 < width && ny2 >= 0 && ny2 < height ? ridgeResponse[ny2 * width + nx2] : 0;

        // Keep only local maxima
        if (response >= neighbor1 && response >= neighbor2) {
          ridgeImage[idx] = 255;
          ridgeMagnitude[idx] = Math.min(65535, Math.round(response * 100));
        }
      }
    }

    // Step 5: Find anchor points and trace ridge chains (similar to EDLines)
    const maxAnchors = Math.ceil(size / (scanInterval * scanInterval * 4));
    const anchorX = new Uint16Array(maxAnchors);
    const anchorY = new Uint16Array(maxAnchors);
    const anchorMag = new Uint32Array(maxAnchors);
    let anchorCount = 0;

    for (let y = 2; y < height - 2; y += scanInterval) {
      const rowOffset = y * width;
      for (let x = 2; x < width - 2; x += scanInterval) {
        const idx = rowOffset + x;
        if (ridgeImage[idx] === 0) continue;

        const mag = ridgeMagnitude[idx];

        // Check if this is a local maximum among scanned points
        let isAnchor = true;
        for (let dy = -1; dy <= 1 && isAnchor; dy++) {
          for (let dx = -1; dx <= 1 && isAnchor; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nidx = (y + dy) * width + (x + dx);
            if (ridgeMagnitude[nidx] > mag) {
              isAnchor = false;
            }
          }
        }

        if (isAnchor && anchorCount < maxAnchors) {
          anchorX[anchorCount] = x;
          anchorY[anchorCount] = y;
          anchorMag[anchorCount] = mag;
          anchorCount++;
        }
      }
    }

    // Sort anchors by magnitude (descending) using O(n log n) sort
    this.sortAnchorsByMagnitude(anchorX, anchorY, anchorMag, anchorCount);

    // Step 6: Trace ridge chains from anchors
    const visited = new Uint8Array(size);
    const maxChainSize = 2000;
    const chainBuffer = new Int16Array(maxChainSize * 2);

    for (let a = 0; a < anchorCount; a++) {
      const startX = anchorX[a];
      const startY = anchorY[a];
      const startIdx = startY * width + startX;

      if (visited[startIdx]) continue;
      visited[startIdx] = 1;

      let chainLen = 0;

      // Trace backward
      const backwardLen = this.traceRidgeChain(startX, startY, -1, ridgeImage, ridgeDirection, visited, width, height, chainBuffer, 0, maxChainSize);

      // Reverse backward chain
      for (let i = 0; i < backwardLen; i++) {
        const srcIdx = (backwardLen - 1 - i) * 2;
        chainBuffer[chainLen * 2] = chainBuffer[srcIdx];
        chainBuffer[chainLen * 2 + 1] = chainBuffer[srcIdx + 1];
        chainLen++;
      }

      // Add anchor point
      chainBuffer[chainLen * 2] = startX;
      chainBuffer[chainLen * 2 + 1] = startY;
      chainLen++;

      // Trace forward
      const forwardLen = this.traceRidgeChain(startX, startY, 1, ridgeImage, ridgeDirection, visited, width, height, chainBuffer, chainLen, maxChainSize);
      chainLen += forwardLen;

      // Fit line segments
      if (chainLen >= minLineLength) {
        this.fitLineSegmentsFast(chainBuffer, chainLen, lineFitErrorThreshold, minLineLength, lines);
      }
    }

    return { lines, ridgeImage };
  }

  /**
   * Frangi Vesselness Filter - Omnidirectional ridge/line detection
   *
   * Implements the Frangi vesselness filter for detecting tubular/ridge structures
   * in any orientation. Uses multi-scale Hessian eigenvalue analysis.
   *
   * The filter is rotation-invariant and works by:
   * 1. Computing Hessian matrix at multiple scales (Gaussian smoothing)
   * 2. Analyzing eigenvalues to detect "vesselness" (line-like structures)
   * 3. Combining responses across scales (max response)
   * 4. Tracing and fitting line segments
   *
   * Reference: Frangi et al., "Multiscale vessel enhancement filtering" (1998)
   */
  private frangiFilter(
    grayscale: Uint8ClampedArray,
    width: number,
    height: number,
    scales: number[],
    beta: number,
    cParam: number | undefined,
    blackRidges: boolean,
    threshold: number,
    minLineLength: number,
    lineFitErrorThreshold: number,
    scanInterval: number,
  ): {
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
    vesselnessImage: Uint8ClampedArray;
  } {
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const size = width * height;

    // Combined vesselness response across all scales (max)
    const vesselness = new Float32Array(size);
    const ridgeDirection = new Float32Array(size);

    // Beta squared for blob suppression term
    const betaSq = 2 * beta * beta;

    // Process each scale
    for (const sigma of scales) {
      // Step 1: Compute Gaussian-smoothed image and its derivatives
      // Using separable convolution for efficiency
      const { dxx, dxy, dyy } = this.computeHessianAtScale(grayscale, width, height, sigma);

      // Calculate c (noise threshold) if not provided - use half of max Frobenius norm
      let c = cParam;
      if (c === undefined) {
        let maxFrobenius = 0;
        for (let i = 0; i < size; i++) {
          const frobenius = Math.sqrt(dxx[i] * dxx[i] + 2 * dxy[i] * dxy[i] + dyy[i] * dyy[i]);
          if (frobenius > maxFrobenius) maxFrobenius = frobenius;
        }
        c = maxFrobenius / 2;
      }
      const cSq = 2 * c * c;

      // Step 2: Compute eigenvalues and vesselness at each pixel
      for (let y = 2; y < height - 2; y++) {
        const rowOffset = y * width;
        for (let x = 2; x < width - 2; x++) {
          const idx = rowOffset + x;

          const hxx = dxx[idx];
          const hxy = dxy[idx];
          const hyy = dyy[idx];

          // Eigenvalues of 2x2 symmetric matrix using closed-form solution
          // λ = (trace ± sqrt(trace² - 4*det)) / 2
          const trace = hxx + hyy;
          const det = hxx * hyy - hxy * hxy;
          const discriminant = trace * trace - 4 * det;

          if (discriminant < 0) continue;

          const sqrtDisc = Math.sqrt(discriminant);
          let lambda1 = (trace + sqrtDisc) / 2;
          let lambda2 = (trace - sqrtDisc) / 2;

          // Sort by absolute value: |lambda1| <= |lambda2|
          if (Math.abs(lambda1) > Math.abs(lambda2)) {
            const temp = lambda1;
            lambda1 = lambda2;
            lambda2 = temp;
          }

          // For bright ridges on dark background, lambda2 should be negative (concave down)
          // For dark ridges on bright background, lambda2 should be positive (concave up)
          if (blackRidges) {
            if (lambda2 >= 0) continue; // Skip non-ridge points
          } else {
            if (lambda2 <= 0) continue;
          }

          // Frangi vesselness formula:
          // V = exp(-Rb²/2β²) * (1 - exp(-S²/2c²))
          // where Rb = λ1/λ2 (blobness ratio) and S = ||H||_F (structure)

          // Rb: ratio that distinguishes line from blob (0 for ideal line)
          const rb = lambda1 / lambda2;
          const rbSq = rb * rb;

          // S: Frobenius norm - measures overall structure strength
          const s = Math.sqrt(lambda1 * lambda1 + lambda2 * lambda2);
          const sSq = s * s;

          // Vesselness response
          const v = Math.exp(-rbSq / betaSq) * (1 - Math.exp(-sSq / cSq));

          // Keep max response across scales
          if (v > vesselness[idx]) {
            vesselness[idx] = v;

            // Compute ridge direction from eigenvector of smaller eigenvalue
            // The eigenvector points along the ridge
            let dirX: number, dirY: number;
            if (Math.abs(hxy) > 1e-10) {
              // Eigenvector for lambda1 (smaller absolute eigenvalue)
              dirX = lambda1 - hyy;
              dirY = hxy;
            } else {
              // Diagonal matrix
              if (Math.abs(hxx) < Math.abs(hyy)) {
                dirX = 1;
                dirY = 0;
              } else {
                dirX = 0;
                dirY = 1;
              }
            }
            const dirLen = Math.sqrt(dirX * dirX + dirY * dirY);
            if (dirLen > 1e-10) {
              ridgeDirection[idx] = Math.atan2(dirY / dirLen, dirX / dirLen);
            }
          }
        }
      }
    }

    // Step 3: Non-maximum suppression and thresholding
    const ridgeImage = new Uint8ClampedArray(size);
    const ridgeMagnitude = new Float32Array(size);

    for (let y = 2; y < height - 2; y++) {
      const rowOffset = y * width;
      for (let x = 2; x < width - 2; x++) {
        const idx = rowOffset + x;
        const v = vesselness[idx];

        if (v < threshold) continue;

        // Non-maximum suppression perpendicular to ridge
        const ridgeDir = ridgeDirection[idx];
        const perpX = -Math.sin(ridgeDir);
        const perpY = Math.cos(ridgeDir);

        const nx1 = Math.round(x + perpX);
        const ny1 = Math.round(y + perpY);
        const nx2 = Math.round(x - perpX);
        const ny2 = Math.round(y - perpY);

        const neighbor1 = nx1 >= 0 && nx1 < width && ny1 >= 0 && ny1 < height ? vesselness[ny1 * width + nx1] : 0;
        const neighbor2 = nx2 >= 0 && nx2 < width && ny2 >= 0 && ny2 < height ? vesselness[ny2 * width + nx2] : 0;

        if (v >= neighbor1 && v >= neighbor2) {
          ridgeImage[idx] = Math.min(255, Math.round(v * 255));
          ridgeMagnitude[idx] = v;
        }
      }
    }

    // Step 4: Find anchor points and trace chains (reuse ridge tracing logic)
    const maxAnchors = Math.ceil(size / (scanInterval * scanInterval * 4));
    const anchorX = new Uint16Array(maxAnchors);
    const anchorY = new Uint16Array(maxAnchors);
    const anchorMag = new Float32Array(maxAnchors);
    let anchorCount = 0;

    for (let y = 2; y < height - 2; y += scanInterval) {
      const rowOffset = y * width;
      for (let x = 2; x < width - 2; x += scanInterval) {
        const idx = rowOffset + x;
        if (ridgeImage[idx] === 0) continue;

        const mag = ridgeMagnitude[idx];

        // Check if local maximum among neighbors
        let isAnchor = true;
        for (let dy = -1; dy <= 1 && isAnchor; dy++) {
          for (let dx = -1; dx <= 1 && isAnchor; dx++) {
            if (dx === 0 && dy === 0) continue;
            const nidx = (y + dy) * width + (x + dx);
            if (ridgeMagnitude[nidx] > mag) {
              isAnchor = false;
            }
          }
        }

        if (isAnchor && anchorCount < maxAnchors) {
          anchorX[anchorCount] = x;
          anchorY[anchorCount] = y;
          anchorMag[anchorCount] = mag;
          anchorCount++;
        }
      }
    }

    // Sort anchors by magnitude (descending) using O(n log n) sort
    this.sortAnchorsByMagnitude(anchorX, anchorY, anchorMag, anchorCount);

    // Step 5: Trace chains from anchors
    const visited = new Uint8Array(size);
    const maxChainSize = 2000;
    const chainBuffer = new Int16Array(maxChainSize * 2);

    for (let a = 0; a < anchorCount; a++) {
      const startX = anchorX[a];
      const startY = anchorY[a];
      const startIdx = startY * width + startX;

      if (visited[startIdx]) continue;
      visited[startIdx] = 1;

      let chainLen = 0;

      // Trace backward
      const backwardLen = this.traceRidgeChain(startX, startY, -1, ridgeImage, ridgeDirection, visited, width, height, chainBuffer, 0, maxChainSize);

      // Reverse backward chain
      for (let i = 0; i < backwardLen; i++) {
        const srcIdx = (backwardLen - 1 - i) * 2;
        chainBuffer[chainLen * 2] = chainBuffer[srcIdx];
        chainBuffer[chainLen * 2 + 1] = chainBuffer[srcIdx + 1];
        chainLen++;
      }

      // Add anchor point
      chainBuffer[chainLen * 2] = startX;
      chainBuffer[chainLen * 2 + 1] = startY;
      chainLen++;

      // Trace forward
      const forwardLen = this.traceRidgeChain(startX, startY, 1, ridgeImage, ridgeDirection, visited, width, height, chainBuffer, chainLen, maxChainSize);
      chainLen += forwardLen;

      // Fit line segments
      if (chainLen >= minLineLength) {
        this.fitLineSegmentsFast(chainBuffer, chainLen, lineFitErrorThreshold, minLineLength, lines);
      }
    }

    return { lines, vesselnessImage: ridgeImage };
  }

  /**
   * ELSED: Enhanced Line SEgment Drawing
   *
   * The fastest line segment detector in the literature. Key improvement over EDLines:
   * - Can jump over small gaps/discontinuities during segment growing
   * - Uses L1 gradient norm (faster than L2)
   * - Orientation validation for better accuracy
   *
   * Reference: Suárez et al., "ELSED: Enhanced Line SEgment Drawing" (2022)
   */
  private elsed(
    grayscale: Uint8ClampedArray,
    width: number,
    height: number,
    gradientThreshold: number,
    anchorThreshold: number,
    scanInterval: number,
    minLineLength: number,
    lineFitErrorThreshold: number,
    maxGapSize: number,
    validationThreshold: number,
  ): {
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>;
    edgeImage: Uint8ClampedArray;
  } {
    const lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const size = width * height;

    // Step 1: Compute gradients using Sobel (L1 norm for speed)
    const gradientMag = new Uint16Array(size);
    const gradientDir = new Uint8Array(size); // 0 = horizontal edge (|dy| > |dx|), 1 = vertical edge
    const gradientDx = new Int16Array(size);
    const gradientDy = new Int16Array(size);

    for (let y = 1; y < height - 1; y++) {
      const rowOffset = y * width;
      const rowAbove = (y - 1) * width;
      const rowBelow = (y + 1) * width;

      for (let x = 1; x < width - 1; x++) {
        // Sobel kernels - unrolled for speed
        const tl = grayscale[rowAbove + x - 1];
        const tc = grayscale[rowAbove + x];
        const tr = grayscale[rowAbove + x + 1];
        const ml = grayscale[rowOffset + x - 1];
        const mr = grayscale[rowOffset + x + 1];
        const bl = grayscale[rowBelow + x - 1];
        const bc = grayscale[rowBelow + x];
        const br = grayscale[rowBelow + x + 1];

        const dx = -tl + tr - 2 * ml + 2 * mr - bl + br;
        const dy = -tl - 2 * tc - tr + bl + 2 * bc + br;

        const idx = rowOffset + x;
        // L1 norm is faster than L2
        const mag = Math.abs(dx) + Math.abs(dy);
        gradientMag[idx] = mag > 65535 ? 65535 : mag;
        gradientDx[idx] = dx;
        gradientDy[idx] = dy;
        // Direction: 0 = horizontal edge, 1 = vertical edge, 2 = diagonal edge
        // Use a ratio threshold to detect diagonal edges (when |dx| and |dy| are similar)
        const absDx = Math.abs(dx);
        const absDy = Math.abs(dy);
        const maxAbs = Math.max(absDx, absDy);
        const minAbs = Math.min(absDx, absDy);
        // If the ratio is close to 1 (within ~30%), it's a diagonal edge
        gradientDir[idx] = maxAbs > 0 && minAbs / maxAbs > 0.7 ? 2 : absDx > absDy ? 0 : 1;
      }
    }

    // Step 2: Find anchor points (local maxima in gradient perpendicular to edge direction)
    const maxAnchors = Math.ceil(size / (scanInterval * scanInterval * 4));
    const anchorX = new Uint16Array(maxAnchors);
    const anchorY = new Uint16Array(maxAnchors);
    const anchorMag = new Uint32Array(maxAnchors);
    let anchorCount = 0;

    for (let y = 2; y < height - 2; y += scanInterval) {
      const rowOffset = y * width;

      for (let x = 2; x < width - 2; x += scanInterval) {
        const idx = rowOffset + x;
        const mag = gradientMag[idx];

        if (mag < gradientThreshold) continue;

        const dir = gradientDir[idx];
        let isAnchor: boolean;

        if (dir === 0) {
          // Horizontal edge: compare with vertical neighbors
          const above = gradientMag[(y - 1) * width + x];
          const below = gradientMag[(y + 1) * width + x];
          isAnchor = mag - above >= anchorThreshold && mag - below >= anchorThreshold;
        } else if (dir === 1) {
          // Vertical edge: compare with horizontal neighbors
          const left = gradientMag[rowOffset + x - 1];
          const right = gradientMag[rowOffset + x + 1];
          isAnchor = mag - left >= anchorThreshold && mag - right >= anchorThreshold;
        } else {
          // Diagonal edge (dir === 2): compare with perpendicular diagonal neighbors
          // For a ~45° edge, gradient points ~45° so perpendicular is also diagonal
          const dx = gradientDx[idx];
          const dy = gradientDy[idx];
          // Determine which diagonal to compare based on gradient direction
          // If gradient is top-right to bottom-left, compare top-left and bottom-right
          // If gradient is top-left to bottom-right, compare top-right and bottom-left
          let n1: number, n2: number;
          if ((dx > 0 && dy > 0) || (dx < 0 && dy < 0)) {
            // Gradient along main diagonal, edge perpendicular (anti-diagonal)
            n1 = gradientMag[(y - 1) * width + (x + 1)]; // top-right
            n2 = gradientMag[(y + 1) * width + (x - 1)]; // bottom-left
          } else {
            // Gradient along anti-diagonal, edge perpendicular (main diagonal)
            n1 = gradientMag[(y - 1) * width + (x - 1)]; // top-left
            n2 = gradientMag[(y + 1) * width + (x + 1)]; // bottom-right
          }
          isAnchor = mag - n1 >= anchorThreshold && mag - n2 >= anchorThreshold;
        }

        if (isAnchor && anchorCount < maxAnchors) {
          anchorX[anchorCount] = x;
          anchorY[anchorCount] = y;
          anchorMag[anchorCount] = mag;
          anchorCount++;
        }
      }
    }

    // Sort anchors by magnitude (descending) using O(n log n) sort
    this.sortAnchorsByMagnitude(anchorX, anchorY, anchorMag, anchorCount);

    // Step 3: Draw edge chains from anchors with gap jumping
    const edgeImage = new Uint8ClampedArray(size);
    const visited = new Uint8Array(size);
    const maxChainSize = 2000;
    const chainBuffer = new Int16Array(maxChainSize * 2);

    for (let a = 0; a < anchorCount; a++) {
      const startX = anchorX[a];
      const startY = anchorY[a];
      const startIdx = startY * width + startX;

      if (visited[startIdx]) continue;
      visited[startIdx] = 1;

      let chainLen = 0;

      // Trace in both directions with gap jumping
      const backwardLen = this.traceElsedChain(startX, startY, -1, gradientMag, gradientDir, gradientDx, gradientDy, gradientThreshold, visited, width, height, chainBuffer, 0, maxChainSize, maxGapSize);

      // Reverse backward chain
      for (let i = 0; i < backwardLen; i++) {
        const srcIdx = (backwardLen - 1 - i) * 2;
        chainBuffer[chainLen * 2] = chainBuffer[srcIdx];
        chainBuffer[chainLen * 2 + 1] = chainBuffer[srcIdx + 1];
        chainLen++;
      }

      // Add anchor point
      chainBuffer[chainLen * 2] = startX;
      chainBuffer[chainLen * 2 + 1] = startY;
      chainLen++;

      // Trace forward with gap jumping
      const forwardLen = this.traceElsedChain(startX, startY, 1, gradientMag, gradientDir, gradientDx, gradientDy, gradientThreshold, visited, width, height, chainBuffer, chainLen, maxChainSize, maxGapSize);
      chainLen += forwardLen;

      // Mark edge pixels
      for (let i = 0; i < chainLen; i++) {
        const px = chainBuffer[i * 2];
        const py = chainBuffer[i * 2 + 1];
        edgeImage[py * width + px] = 255;
      }

      // Fit line segments
      if (chainLen >= minLineLength) {
        const segmentLines = this.fitAndValidateSegments(chainBuffer, chainLen, lineFitErrorThreshold, minLineLength, gradientDx, gradientDy, width, validationThreshold);
        lines.push(...segmentLines);
      }
    }

    return { lines, edgeImage };
  }

  /**
   * Trace edge chain with gap jumping capability (ELSED's key innovation)
   */
  private traceElsedChain(
    startX: number,
    startY: number,
    direction: number,
    gradientMag: Uint16Array,
    gradientDir: Uint8Array,
    gradientDx: Int16Array,
    gradientDy: Int16Array,
    gradientThreshold: number,
    visited: Uint8Array,
    width: number,
    height: number,
    chainBuffer: Int16Array,
    chainOffset: number,
    maxChainSize: number,
    maxGapSize: number,
  ): number {
    // Direction offsets for 8-connectivity
    const DX = [1, 1, 0, -1, -1, -1, 0, 1];
    const DY = [0, -1, -1, -1, 0, 1, 1, 1];

    let x = startX;
    let y = startY;
    let len = 0;
    const maxLen = maxChainSize - chainOffset;

    while (len < maxLen) {
      const idx = y * width + x;
      const dir = gradientDir[idx];
      const dx = gradientDx[idx];
      const dy = gradientDy[idx];

      // Compute edge direction (perpendicular to gradient)
      let edgeDir: number;
      if (direction > 0) {
        edgeDir = this.quantizeDirection(-dy, dx);
      } else {
        edgeDir = this.quantizeDirection(dy, -dx);
      }

      // Try to find next pixel: first without gap, then with gap jumping
      let found = false;
      let bestX = 0,
        bestY = 0;

      // Try direct neighbors first (no gap)
      const dirs = [edgeDir, (edgeDir + 1) & 7, (edgeDir + 7) & 7];

      for (const d of dirs) {
        const nx = x + DX[d];
        const ny = y + DY[d];

        if (nx < 1 || nx >= width - 1 || ny < 1 || ny >= height - 1) continue;
        const nidx = ny * width + nx;
        if (visited[nidx]) continue;
        if (gradientMag[nidx] < gradientThreshold) continue;

        // Check gradient direction consistency with tolerance for diagonal edges
        // dir: 0 = horizontal edge, 1 = vertical edge, 2 = diagonal edge
        // Diagonal edges (2) can connect to any direction since they're at the boundary
        const ndir = gradientDir[nidx];
        const dirsCompatible = ndir === dir || dir === 2 || ndir === 2;
        if (!dirsCompatible) continue;

        visited[nidx] = 1;
        bestX = nx;
        bestY = ny;
        found = true;
        break;
      }

      // Gap jumping: if no direct neighbor found, try to jump over small gaps
      if (!found && maxGapSize > 0) {
        for (let gap = 2; gap <= maxGapSize + 1 && !found; gap++) {
          for (const d of dirs) {
            const nx = x + DX[d] * gap;
            const ny = y + DY[d] * gap;

            if (nx < 1 || nx >= width - 1 || ny < 1 || ny >= height - 1) continue;
            const nidx = ny * width + nx;
            if (visited[nidx]) continue;
            if (gradientMag[nidx] < gradientThreshold) continue;

            // Check gradient direction consistency with tolerance for diagonal edges
            const ndir = gradientDir[nidx];
            const dirsCompatible = ndir === dir || dir === 2 || ndir === 2;
            if (!dirsCompatible) continue;

            // Found a pixel after gap - mark intermediate pixels as visited
            // but don't add them to the chain
            for (let g = 1; g < gap; g++) {
              const gx = x + DX[d] * g;
              const gy = y + DY[d] * g;
              if (gx >= 0 && gx < width && gy >= 0 && gy < height) {
                visited[gy * width + gx] = 1;
              }
            }

            visited[nidx] = 1;
            bestX = nx;
            bestY = ny;
            found = true;
            break;
          }
        }
      }

      if (!found) break;

      chainBuffer[(chainOffset + len) * 2] = bestX;
      chainBuffer[(chainOffset + len) * 2 + 1] = bestY;
      x = bestX;
      y = bestY;
      len++;
    }

    return len;
  }

  /**
   * Fit line segments and validate orientation consistency (ELSED validation step)
   */
  private fitAndValidateSegments(
    chain: Int16Array,
    chainLen: number,
    errorThreshold: number,
    minLength: number,
    gradientDx: Int16Array,
    gradientDy: Int16Array,
    width: number,
    validationThreshold: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    const output: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const errorThresholdSq = errorThreshold * errorThreshold;

    const fitRecursive = (start: number, end: number) => {
      const count = end - start + 1;
      if (count < minLength) return;

      const x1 = chain[start * 2];
      const y1 = chain[start * 2 + 1];
      const x2 = chain[end * 2];
      const y2 = chain[end * 2 + 1];

      const lineDx = x2 - x1;
      const lineDy = y2 - y1;
      const lineLenSq = lineDx * lineDx + lineDy * lineDy;

      if (lineLenSq < 1) return;

      // Find point with maximum distance from line
      let maxDistSq = 0;
      let maxIdx = start;

      for (let i = start + 1; i < end; i++) {
        const px = chain[i * 2];
        const py = chain[i * 2 + 1];
        const cross = (py - y1) * lineDx - (px - x1) * lineDy;
        const distSq = (cross * cross) / lineLenSq;

        if (distSq > maxDistSq) {
          maxDistSq = distSq;
          maxIdx = i;
        }
      }

      if (maxDistSq > errorThresholdSq) {
        // Split at max distance point
        fitRecursive(start, maxIdx);
        fitRecursive(maxIdx, end);
      } else {
        // Validate segment: check orientation consistency
        if (lineLenSq >= minLength * minLength) {
          // Compute expected gradient direction (perpendicular to line)
          const lineLen = Math.sqrt(lineLenSq);
          const lineNormX = lineDx / lineLen;
          const lineNormY = lineDy / lineLen;
          // Gradient should be perpendicular to line direction
          const expectedGradX = -lineNormY;
          const expectedGradY = lineNormX;

          // Check gradient alignment along the segment
          let alignedCount = 0;
          for (let i = start; i <= end; i++) {
            const px = chain[i * 2];
            const py = chain[i * 2 + 1];
            const idx = py * width + px;

            const gx = gradientDx[idx];
            const gy = gradientDy[idx];
            const gLen = Math.sqrt(gx * gx + gy * gy);

            if (gLen > 0) {
              // Dot product with expected direction (absolute value since gradient can point either way)
              const dot = Math.abs((gx / gLen) * expectedGradX + (gy / gLen) * expectedGradY);
              if (dot > validationThreshold) {
                alignedCount++;
              }
            }
          }

          // Only keep segment if enough pixels have aligned gradients
          const alignmentRatio = alignedCount / count;
          if (alignmentRatio > 0.5) {
            output.push({
              x1,
              y1,
              x2,
              y2,
              votes: count,
            });
          }
        }
      }
    };

    fitRecursive(0, chainLen - 1);
    return output;
  }

  /**
   * Compute Hessian matrix components at a given scale using separable Gaussian derivatives
   * This is the performance-critical part - uses separable convolutions for O(n*k) instead of O(n*k²)
   */
  private computeHessianAtScale(
    grayscale: Uint8ClampedArray,
    width: number,
    height: number,
    sigma: number,
  ): {
    dxx: Float32Array;
    dxy: Float32Array;
    dyy: Float32Array;
  } {
    const size = width * height;
    const kernelRadius = Math.ceil(sigma * 3);
    const kernelSize = kernelRadius * 2 + 1;

    // Pre-compute Gaussian kernel and its derivatives
    const g = new Float32Array(kernelSize); // Gaussian
    const dg = new Float32Array(kernelSize); // First derivative of Gaussian
    const d2g = new Float32Array(kernelSize); // Second derivative of Gaussian

    const sigmaSq = sigma * sigma;
    const sigma4 = sigmaSq * sigmaSq;
    let gSum = 0;

    for (let i = 0; i < kernelSize; i++) {
      const x = i - kernelRadius;
      const xSq = x * x;
      const expTerm = Math.exp(-xSq / (2 * sigmaSq));

      g[i] = expTerm;
      gSum += g[i];

      // First derivative: -x/(σ²) * G(x)
      dg[i] = (-x / sigmaSq) * expTerm;

      // Second derivative: (x²-σ²)/(σ⁴) * G(x)
      d2g[i] = ((xSq - sigmaSq) / sigma4) * expTerm;
    }

    // Normalize Gaussian kernel
    for (let i = 0; i < kernelSize; i++) {
      g[i] /= gSum;
    }

    // Scale derivatives by sigma for scale normalization (Lindeberg)
    const scaleNorm = sigmaSq;
    for (let i = 0; i < kernelSize; i++) {
      dg[i] *= scaleNorm / gSum;
      d2g[i] *= scaleNorm / gSum;
    }

    // Allocate output arrays
    const dxx = new Float32Array(size);
    const dxy = new Float32Array(size);
    const dyy = new Float32Array(size);

    // Temporary buffer for separable convolution
    const temp1 = new Float32Array(size);

    // Helper function for horizontal convolution
    const convolveH = (input: Uint8ClampedArray | Float32Array, output: Float32Array, kernel: Float32Array) => {
      for (let y = 0; y < height; y++) {
        const rowOffset = y * width;
        for (let x = 0; x < width; x++) {
          let sum = 0;
          for (let k = 0; k < kernelSize; k++) {
            const sx = x + k - kernelRadius;
            const clampedX = Math.max(0, Math.min(width - 1, sx));
            sum += input[rowOffset + clampedX] * kernel[k];
          }
          output[rowOffset + x] = sum;
        }
      }
    };

    // Helper function for vertical convolution
    const convolveV = (input: Float32Array, output: Float32Array, kernel: Float32Array) => {
      for (let y = 0; y < height; y++) {
        for (let x = 0; x < width; x++) {
          let sum = 0;
          for (let k = 0; k < kernelSize; k++) {
            const sy = y + k - kernelRadius;
            const clampedY = Math.max(0, Math.min(height - 1, sy));
            sum += input[clampedY * width + x] * kernel[k];
          }
          output[y * width + x] = sum;
        }
      }
    };

    // Compute dxx: d²G/dx² * G (second derivative in x, smoothed in y)
    convolveH(grayscale, temp1, d2g);
    convolveV(temp1, dxx, g);

    // Compute dyy: G * d²G/dy² (smoothed in x, second derivative in y)
    convolveH(grayscale, temp1, g);
    convolveV(temp1, dyy, d2g);

    // Compute dxy: dG/dx * dG/dy (first derivative in both)
    convolveH(grayscale, temp1, dg);
    convolveV(temp1, dxy, dg);

    return { dxx, dxy, dyy };
  }

  /**
   * Gaussian smoothing for ridge detection
   */
  private gaussianSmooth(grayscale: Uint8ClampedArray, width: number, height: number, sigma: number): Float32Array {
    const size = width * height;
    const result = new Float32Array(size);

    // Create 1D Gaussian kernel
    const kernelRadius = Math.ceil(sigma * 3);
    const kernelSize = kernelRadius * 2 + 1;
    const kernel = new Float32Array(kernelSize);
    let kernelSum = 0;

    for (let i = 0; i < kernelSize; i++) {
      const x = i - kernelRadius;
      kernel[i] = Math.exp(-(x * x) / (2 * sigma * sigma));
      kernelSum += kernel[i];
    }

    // Normalize kernel
    for (let i = 0; i < kernelSize; i++) {
      kernel[i] /= kernelSum;
    }

    // Separable convolution: horizontal pass
    const temp = new Float32Array(size);
    for (let y = 0; y < height; y++) {
      const rowOffset = y * width;
      for (let x = 0; x < width; x++) {
        let sum = 0;
        for (let k = 0; k < kernelSize; k++) {
          const sx = x + k - kernelRadius;
          const clampedX = Math.max(0, Math.min(width - 1, sx));
          sum += grayscale[rowOffset + clampedX] * kernel[k];
        }
        temp[rowOffset + x] = sum;
      }
    }

    // Vertical pass
    for (let y = 0; y < height; y++) {
      for (let x = 0; x < width; x++) {
        let sum = 0;
        for (let k = 0; k < kernelSize; k++) {
          const sy = y + k - kernelRadius;
          const clampedY = Math.max(0, Math.min(height - 1, sy));
          sum += temp[clampedY * width + x] * kernel[k];
        }
        result[y * width + x] = sum;
      }
    }

    return result;
  }

  /**
   * Trace ridge chain following ridge direction
   */
  private traceRidgeChain(
    startX: number,
    startY: number,
    direction: number,
    ridgeImage: Uint8ClampedArray,
    ridgeDirection: Float32Array,
    visited: Uint8Array,
    width: number,
    height: number,
    chainBuffer: Int16Array,
    chainOffset: number,
    maxChainSize: number,
  ): number {
    const DX = [1, 1, 0, -1, -1, -1, 0, 1];
    const DY = [0, -1, -1, -1, 0, 1, 1, 1];

    let x = startX;
    let y = startY;
    let len = 0;
    const maxLen = maxChainSize - chainOffset;

    while (len < maxLen) {
      const idx = y * width + x;
      const ridgeDir = ridgeDirection[idx];

      // Compute direction along ridge
      let targetDir: number;
      if (direction > 0) {
        targetDir = ridgeDir;
      } else {
        targetDir = ridgeDir + Math.PI;
      }

      // Quantize to 8-connected direction
      const quantized = Math.round(((targetDir + Math.PI) / (2 * Math.PI)) * 8) & 7;

      // Try main direction and neighbors
      let found = false;
      const dirs = [quantized, (quantized + 1) & 7, (quantized + 7) & 7];

      for (let d = 0; d < 3; d++) {
        const dir = dirs[d];
        const nx = x + DX[dir];
        const ny = y + DY[dir];

        if (nx < 1 || nx >= width - 1 || ny < 1 || ny >= height - 1) continue;

        const nidx = ny * width + nx;
        if (visited[nidx]) continue;
        if (ridgeImage[nidx] === 0) continue;

        visited[nidx] = 1;
        chainBuffer[(chainOffset + len) * 2] = nx;
        chainBuffer[(chainOffset + len) * 2 + 1] = ny;
        x = nx;
        y = ny;
        len++;
        found = true;
        break;
      }

      if (!found) break;
    }

    return len;
  }

  /**
   * Fast edge chain tracing using typed arrays
   */
  private traceEdgeChainFast(
    startX: number,
    startY: number,
    direction: number,
    gradientGx: Int16Array,
    gradientGy: Int16Array,
    gradientMag: Uint16Array,
    gradientThresholdSq: number,
    visited: Uint8Array,
    width: number,
    height: number,
    chainBuffer: Int16Array,
    chainOffset: number,
    maxChainSize: number,
  ): number {
    const DX = [1, 1, 0, -1, -1, -1, 0, 1];
    const DY = [0, -1, -1, -1, 0, 1, 1, 1];

    let x = startX;
    let y = startY;
    let len = 0;
    const maxLen = maxChainSize - chainOffset;

    while (len < maxLen) {
      const idx = y * width + x;
      const gx = gradientGx[idx];
      const gy = gradientGy[idx];

      // Compute edge direction from gradient (perpendicular)
      // Use lookup table approach: quantize to 8 directions
      let edgeDir: number;
      if (direction > 0) {
        // Forward: perpendicular clockwise
        edgeDir = this.quantizeDirection(-gy, gx);
      } else {
        // Backward: perpendicular counter-clockwise
        edgeDir = this.quantizeDirection(gy, -gx);
      }

      // Try main direction first, then ±1 for curved edges
      let found = false;
      const dirs = [edgeDir, (edgeDir + 1) & 7, (edgeDir + 7) & 7];

      for (let d = 0; d < 3; d++) {
        const dir = dirs[d];
        const nx = x + DX[dir];
        const ny = y + DY[dir];

        if (nx < 1 || nx >= width - 1 || ny < 1 || ny >= height - 1) continue;

        const nidx = ny * width + nx;
        if (visited[nidx]) continue;
        if (gradientMag[nidx] < gradientThresholdSq) continue;

        visited[nidx] = 1;
        chainBuffer[(chainOffset + len) * 2] = nx;
        chainBuffer[(chainOffset + len) * 2 + 1] = ny;
        x = nx;
        y = ny;
        len++;
        found = true;
        break;
      }

      if (!found) break;
    }

    return len;
  }

  /**
   * Sort anchors by magnitude (descending) using O(n log n) sort.
   * Modifies arrays in place.
   */
  private sortAnchorsByMagnitude(anchorX: Uint16Array, anchorY: Uint16Array, anchorMag: Uint32Array | Float32Array, anchorCount: number): void {
    if (anchorCount <= 1) return;

    // Create index array and sort by magnitude
    const indices = new Uint32Array(anchorCount);
    for (let i = 0; i < anchorCount; i++) indices[i] = i;

    // Sort indices by magnitude descending using built-in sort (Timsort, O(n log n))
    const magRef = anchorMag;
    indices.sort((a, b) => magRef[b] - magRef[a]);

    // Reorder arrays according to sorted indices
    const sortedX = new Uint16Array(anchorCount);
    const sortedY = new Uint16Array(anchorCount);
    for (let i = 0; i < anchorCount; i++) {
      const srcIdx = indices[i];
      sortedX[i] = anchorX[srcIdx];
      sortedY[i] = anchorY[srcIdx];
    }

    // Handle magnitude array based on type
    if (anchorMag instanceof Float32Array) {
      const sortedMag = new Float32Array(anchorCount);
      for (let i = 0; i < anchorCount; i++) {
        sortedMag[i] = anchorMag[indices[i]];
      }
      for (let i = 0; i < anchorCount; i++) {
        anchorMag[i] = sortedMag[i];
      }
    } else {
      const sortedMag = new Uint32Array(anchorCount);
      for (let i = 0; i < anchorCount; i++) {
        sortedMag[i] = anchorMag[indices[i]];
      }
      for (let i = 0; i < anchorCount; i++) {
        anchorMag[i] = sortedMag[i];
      }
    }

    // Copy back X and Y
    for (let i = 0; i < anchorCount; i++) {
      anchorX[i] = sortedX[i];
      anchorY[i] = sortedY[i];
    }
  }

  /**
   * Quantize direction vector to 8-connected direction index (0-7)
   */
  private quantizeDirection(dx: number, dy: number): number {
    // Fast approximation using comparisons instead of atan2
    const absDx = dx < 0 ? -dx : dx;
    const absDy = dy < 0 ? -dy : dy;

    if (absDx > 2 * absDy) {
      // Mostly horizontal
      return dx > 0 ? 0 : 4;
    } else if (absDy > 2 * absDx) {
      // Mostly vertical
      return dy < 0 ? 2 : 6;
    } else {
      // Diagonal
      if (dx > 0) {
        return dy < 0 ? 1 : 7;
      } else {
        return dy < 0 ? 3 : 5;
      }
    }
  }

  /**
   * Fast line segment fitting using Douglas-Peucker on flat array
   */
  private fitLineSegmentsFast(chain: Int16Array, chainLen: number, errorThreshold: number, minLength: number, output: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>): void {
    const errorThresholdSq = errorThreshold * errorThreshold;

    const fitRecursive = (start: number, end: number) => {
      const count = end - start + 1;
      if (count < minLength) return;

      const x1 = chain[start * 2];
      const y1 = chain[start * 2 + 1];
      const x2 = chain[end * 2];
      const y2 = chain[end * 2 + 1];

      const lineDx = x2 - x1;
      const lineDy = y2 - y1;
      const lineLenSq = lineDx * lineDx + lineDy * lineDy;

      if (lineLenSq < 1) return;

      // Find point with maximum distance from line
      let maxDistSq = 0;
      let maxIdx = start;

      for (let i = start + 1; i < end; i++) {
        const px = chain[i * 2];
        const py = chain[i * 2 + 1];
        // Cross product gives signed area, divide by length for distance
        const cross = (py - y1) * lineDx - (px - x1) * lineDy;
        const distSq = (cross * cross) / lineLenSq;

        if (distSq > maxDistSq) {
          maxDistSq = distSq;
          maxIdx = i;
        }
      }

      if (maxDistSq > errorThresholdSq) {
        // Split at max distance point
        fitRecursive(start, maxIdx);
        fitRecursive(maxIdx, end);
      } else {
        // Check segment length
        if (lineLenSq >= minLength * minLength) {
          output.push({
            x1,
            y1,
            x2,
            y2,
            votes: count,
          });
        }
      }
    };

    fitRecursive(0, chainLen - 1);
  }

  /**
   * Combine multiple small collinear segments into single longer lines.
   * Groups segments by orientation, then checks if they lie on the same line
   * with small gaps between them.
   */
  private combineCollinearSegments(
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>,
    angleThresholdDeg: number,
    perpendicularThreshold: number,
    gapThreshold: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (lines.length < 2) return lines;

    const angleThresh = angleThresholdDeg * (Math.PI / 180);
    const result: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const used = new Array(lines.length).fill(false);

    // For each line, try to find all collinear segments and combine them
    for (let i = 0; i < lines.length; i++) {
      if (used[i]) continue;

      // Start a group with this line
      const group: number[] = [i];
      used[i] = true;

      const baseAngle = Math.atan2(lines[i].y2 - lines[i].y1, lines[i].x2 - lines[i].x1);

      // Find all lines with similar angle
      for (let j = i + 1; j < lines.length; j++) {
        if (used[j]) continue;

        const angle = Math.atan2(lines[j].y2 - lines[j].y1, lines[j].x2 - lines[j].x1);
        let angleDiff = Math.abs(baseAngle - angle);
        if (angleDiff > Math.PI / 2) angleDiff = Math.PI - angleDiff;

        if (angleDiff < angleThresh) {
          group.push(j);
        }
      }

      if (group.length === 1) {
        // No similar angles found, keep original line
        result.push(lines[i]);
        continue;
      }

      // Collect all endpoints from the group
      const points: Array<{ x: number; y: number; lineIdx: number; isStart: boolean }> = [];
      for (const idx of group) {
        points.push({ x: lines[idx].x1, y: lines[idx].y1, lineIdx: idx, isStart: true });
        points.push({ x: lines[idx].x2, y: lines[idx].y2, lineIdx: idx, isStart: false });
      }

      // Fit a line through all points using least squares to get the "master" line direction
      const fitResult = this.fitLineLeastSquares(points.map((p) => ({ x: p.x, y: p.y })));
      if (!fitResult) {
        // Fallback: keep original lines
        for (const idx of group) {
          if (idx !== i) used[idx] = false;
        }
        result.push(lines[i]);
        continue;
      }

      const { point: centroid, direction } = fitResult;
      const dirLen = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
      const dirX = direction.x / dirLen;
      const dirY = direction.y / dirLen;

      // Check perpendicular distance of each segment's endpoints to the fitted line
      // and project them onto the line direction
      const projections: Array<{
        lineIdx: number;
        minProj: number;
        maxProj: number;
        perpDist: number;
      }> = [];

      for (const idx of group) {
        const line = lines[idx];

        // Project both endpoints onto the fitted line
        const proj1 = (line.x1 - centroid.x) * dirX + (line.y1 - centroid.y) * dirY;
        const proj2 = (line.x2 - centroid.x) * dirX + (line.y2 - centroid.y) * dirY;

        // Perpendicular distance (cross product)
        const perp1 = Math.abs((line.x1 - centroid.x) * dirY - (line.y1 - centroid.y) * dirX);
        const perp2 = Math.abs((line.x2 - centroid.x) * dirY - (line.y2 - centroid.y) * dirX);
        const maxPerp = Math.max(perp1, perp2);

        projections.push({
          lineIdx: idx,
          minProj: Math.min(proj1, proj2),
          maxProj: Math.max(proj1, proj2),
          perpDist: maxPerp,
        });
      }

      // Filter out segments too far from the fitted line
      const validProjections = projections.filter((p) => p.perpDist <= perpendicularThreshold);

      if (validProjections.length === 0) {
        // No valid segments, keep original
        for (const idx of group) {
          if (idx !== i) used[idx] = false;
        }
        result.push(lines[i]);
        continue;
      }

      // Sort by projection along line direction
      validProjections.sort((a, b) => a.minProj - b.minProj);

      // Greedily combine segments that are continuous (gap < threshold)
      let currentStart = validProjections[0].minProj;
      let currentEnd = validProjections[0].maxProj;
      let currentVotes = lines[validProjections[0].lineIdx].votes;
      const combinedIndices: number[] = [validProjections[0].lineIdx];

      for (let k = 1; k < validProjections.length; k++) {
        const proj = validProjections[k];
        const gap = proj.minProj - currentEnd;

        if (gap <= gapThreshold) {
          // Extend the current combined segment
          currentEnd = Math.max(currentEnd, proj.maxProj);
          currentVotes += lines[proj.lineIdx].votes;
          combinedIndices.push(proj.lineIdx);
          used[proj.lineIdx] = true;
        } else {
          // Gap too large, output current combined segment and start new one
          const startX = centroid.x + dirX * currentStart;
          const startY = centroid.y + dirY * currentStart;
          const endX = centroid.x + dirX * currentEnd;
          const endY = centroid.y + dirY * currentEnd;

          result.push({
            x1: startX,
            y1: startY,
            x2: endX,
            y2: endY,
            votes: currentVotes,
          });

          // Mark used
          for (const idx of combinedIndices) {
            used[idx] = true;
          }

          // Start new segment
          currentStart = proj.minProj;
          currentEnd = proj.maxProj;
          currentVotes = lines[proj.lineIdx].votes;
          combinedIndices.length = 0;
          combinedIndices.push(proj.lineIdx);
        }
      }

      // Output the last combined segment
      const startX = centroid.x + dirX * currentStart;
      const startY = centroid.y + dirY * currentStart;
      const endX = centroid.x + dirX * currentEnd;
      const endY = centroid.y + dirY * currentEnd;

      result.push({
        x1: startX,
        y1: startY,
        x2: endX,
        y2: endY,
        votes: currentVotes,
      });

      // Mark all combined as used
      for (const idx of combinedIndices) {
        used[idx] = true;
      }

      // Any segments that weren't combined (too far perpendicularly) stay unused
      // and will be processed in their own iteration
      for (const proj of projections) {
        if (proj.perpDist > perpendicularThreshold) {
          used[proj.lineIdx] = false;
        }
      }
    }

    return result;
  }

  /**
   * Merge similar lines in Cartesian space using length-weighted averaging.
   * Runs iteratively until no more merges occur or maxIterations is reached.
   * Longer lines have more influence on the final merged position.
   */
  private mergeCartesianLines(
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>,
    width: number,
    height: number,
    angleThreshold: number,
    midpointThreshold: number,
    distanceThreshold: number,
    maxIterations: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (lines.length < 2) return lines;

    let currentLines = [...lines];

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      const result = this.mergeCartesianLinesPass(currentLines, width, height, angleThreshold, midpointThreshold, distanceThreshold);

      // If no merges happened (same number of lines), we're done
      if (result.length === currentLines.length) {
        return result;
      }

      currentLines = result;
    }

    return currentLines;
  }

  /**
   * Single pass of merging similar lines in Cartesian space using length-weighted averaging.
   */
  private mergeCartesianLinesPass(
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>,
    width: number,
    height: number,
    angleThreshold: number,
    midpointThreshold: number,
    distanceThreshold: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (lines.length === 0) return lines;

    // Sort by line length (descending) - longer lines are processed first
    const sortedLines = [...lines].sort((a, b) => {
      const lenA = Math.sqrt(Math.pow(a.x2 - a.x1, 2) + Math.pow(a.y2 - a.y1, 2));
      const lenB = Math.sqrt(Math.pow(b.x2 - b.x1, 2) + Math.pow(b.y2 - b.y1, 2));
      return lenB - lenA;
    });

    const merged: Array<{
      x1: number;
      y1: number;
      x2: number;
      y2: number;
      votes: number;
      totalLength: number; // Track total length for weighted averaging
    }> = [];
    const imageSize = Math.sqrt(width * width + height * height);

    const angleThresh = angleThreshold * (Math.PI / 180);
    const midpointThresh = midpointThreshold ?? imageSize * 0.05;
    const distanceThresh = distanceThreshold ?? imageSize * 0.03;

    // Merge similar lines with length-weighted averaging
    for (const line of sortedLines) {
      const lineLength = Math.sqrt(Math.pow(line.x2 - line.x1, 2) + Math.pow(line.y2 - line.y1, 2));
      let mergedWithExisting = false;

      for (const existing of merged) {
        const lineMidX = (line.x1 + line.x2) / 2;
        const lineMidY = (line.y1 + line.y2) / 2;
        const existingMidX = (existing.x1 + existing.x2) / 2;
        const existingMidY = (existing.y1 + existing.y2) / 2;
        const midDist = Math.sqrt(Math.pow(lineMidX - existingMidX, 2) + Math.pow(lineMidY - existingMidY, 2));

        const lineAngle = Math.atan2(line.y2 - line.y1, line.x2 - line.x1);
        const existingAngle = Math.atan2(existing.y2 - existing.y1, existing.x2 - existing.x1);
        let angleDiff = Math.abs(lineAngle - existingAngle);
        if (angleDiff > Math.PI / 2) angleDiff = Math.PI - angleDiff;

        const dist1 = this.pointToLineDistance({ x: existing.x1, y: existing.y1 }, { x: existing.x2, y: existing.y2 }, { x: line.x1, y: line.y1 });
        const dist2 = this.pointToLineDistance({ x: existing.x1, y: existing.y1 }, { x: existing.x2, y: existing.y2 }, { x: line.x2, y: line.y2 });
        const avgDist = (dist1 + dist2) / 2;

        if (angleDiff < angleThresh && midDist < midpointThresh && avgDist < distanceThresh) {
          // Weighted merge: longer lines have more influence
          const existingWeight = existing.totalLength;
          const newWeight = lineLength;
          const totalWeight = existingWeight + newWeight;

          // Normalize line direction so we're always merging in same direction
          let lx1 = line.x1,
            ly1 = line.y1,
            lx2 = line.x2,
            ly2 = line.y2;

          // Check if line direction is opposite to existing (dot product negative)
          const existingDirX = existing.x2 - existing.x1;
          const existingDirY = existing.y2 - existing.y1;
          const lineDirX = lx2 - lx1;
          const lineDirY = ly2 - ly1;
          const dot = existingDirX * lineDirX + existingDirY * lineDirY;

          if (dot < 0) {
            // Flip line direction
            [lx1, lx2] = [lx2, lx1];
            [ly1, ly2] = [ly2, ly1];
          }

          // Weighted average of endpoints
          existing.x1 = (existing.x1 * existingWeight + lx1 * newWeight) / totalWeight;
          existing.y1 = (existing.y1 * existingWeight + ly1 * newWeight) / totalWeight;
          existing.x2 = (existing.x2 * existingWeight + lx2 * newWeight) / totalWeight;
          existing.y2 = (existing.y2 * existingWeight + ly2 * newWeight) / totalWeight;
          existing.votes = Math.max(existing.votes, line.votes);
          existing.totalLength = totalWeight;

          mergedWithExisting = true;
          break;
        }
      }

      if (!mergedWithExisting) {
        merged.push({ ...line, totalLength: lineLength });
      }
    }

    // Remove totalLength from output
    return merged.map(({ totalLength, ...line }) => line);
  }

  /**
   * Merge overlapping lines that are close together with similar direction.
   * Keeps the outer endpoints to create a single longer line.
   * Runs iteratively until no more merges occur or maxIterations is reached.
   */
  private mergeOverlappingLines(
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>,
    angleThresholdDeg: number,
    distanceThreshold: number,
    projectionThreshold: number,
    maxIterations: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (lines.length < 2) return lines;

    let currentLines = [...lines];

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      const result = this.mergeOverlappingLinesPass(currentLines, angleThresholdDeg, distanceThreshold, projectionThreshold);

      // If no merges happened (same number of lines), we're done
      if (result.length === currentLines.length) {
        return result;
      }

      currentLines = result;
    }

    return currentLines;
  }

  /**
   * Single pass of merging overlapping lines.
   * Combines lines by averaging their perpendicular positions (weighted by length)
   * while keeping the outer bounds along the line direction.
   */
  private mergeOverlappingLinesPass(
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>,
    angleThresholdDeg: number,
    distanceThreshold: number,
    projectionThreshold: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (lines.length < 2) return lines;

    const angleThresh = angleThresholdDeg * (Math.PI / 180);
    const result: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const used = new Array(lines.length).fill(false);

    // Sort by length (descending) to prioritize longer lines for direction
    const sortedIndices = lines
      .map((_, i) => i)
      .sort((a, b) => {
        const lenA = Math.sqrt((lines[a].x2 - lines[a].x1) ** 2 + (lines[a].y2 - lines[a].y1) ** 2);
        const lenB = Math.sqrt((lines[b].x2 - lines[b].x1) ** 2 + (lines[b].y2 - lines[b].y1) ** 2);
        return lenB - lenA;
      });

    for (const i of sortedIndices) {
      if (used[i]) continue;

      const lineA = lines[i];
      used[i] = true;

      // Compute line A's direction and length
      const dxA = lineA.x2 - lineA.x1;
      const dyA = lineA.y2 - lineA.y1;
      const lenA = Math.sqrt(dxA * dxA + dyA * dyA);
      if (lenA < 1) {
        result.push({ ...lineA });
        continue;
      }

      const dirAx = dxA / lenA;
      const dirAy = dyA / lenA;
      const angleA = Math.atan2(dyA, dxA);

      // Perpendicular direction (90 degrees rotated)
      const perpX = -dirAy;
      const perpY = dirAx;

      // Use lineA's start point as the origin for projection
      const originX = lineA.x1;
      const originY = lineA.y1;

      // Collect all lines to merge (including lineA)
      const linesToMerge: Array<{
        length: number;
        perpOffset: number; // Perpendicular offset from lineA
      }> = [];

      // Add lineA with zero perpendicular offset
      linesToMerge.push({ length: lenA, perpOffset: 0 });

      // Track min/max projections along lineA's direction
      let minProj = 0;
      let maxProj = lenA;
      let totalVotes = lineA.votes;

      // Find all lines that should merge with lineA
      for (const j of sortedIndices) {
        if (used[j]) continue;

        const lineB = lines[j];

        // Check angle similarity
        const dxB = lineB.x2 - lineB.x1;
        const dyB = lineB.y2 - lineB.y1;
        const lenB = Math.sqrt(dxB * dxB + dyB * dyB);
        const angleB = Math.atan2(dyB, dxB);

        let angleDiff = Math.abs(angleA - angleB);
        if (angleDiff > Math.PI / 2) angleDiff = Math.PI - angleDiff;

        if (angleDiff > angleThresh) continue;

        // Calculate perpendicular distance of lineB's midpoint to lineA
        const midBx = (lineB.x1 + lineB.x2) / 2;
        const midBy = (lineB.y1 + lineB.y2) / 2;
        const perpDistMid = (midBx - originX) * perpX + (midBy - originY) * perpY;

        if (Math.abs(perpDistMid) > distanceThreshold) continue;

        // Project lineB's endpoints onto lineA's direction
        const projB1 = (lineB.x1 - originX) * dirAx + (lineB.y1 - originY) * dirAy;
        const projB2 = (lineB.x2 - originX) * dirAx + (lineB.y2 - originY) * dirAy;

        const minProjB = Math.min(projB1, projB2);
        const maxProjB = Math.max(projB1, projB2);

        // Calculate overlap (positive) or gap (negative)
        const overlapStart = Math.max(minProj, minProjB);
        const overlapEnd = Math.min(maxProj, maxProjB);
        const overlap = overlapEnd - overlapStart;

        if (overlap < projectionThreshold) continue;

        // Lines should be merged
        linesToMerge.push({ length: lenB, perpOffset: perpDistMid });
        minProj = Math.min(minProj, minProjB);
        maxProj = Math.max(maxProj, maxProjB);
        totalVotes += lineB.votes;
        used[j] = true;
      }

      // Calculate weighted average perpendicular offset based on line lengths
      let totalLength = 0;
      let weightedPerpOffset = 0;
      for (const item of linesToMerge) {
        totalLength += item.length;
        weightedPerpOffset += item.perpOffset * item.length;
      }
      const avgPerpOffset = totalLength > 0 ? weightedPerpOffset / totalLength : 0;

      // Create merged line: use averaged perpendicular position with outer bounds
      const x1 = originX + dirAx * minProj + perpX * avgPerpOffset;
      const y1 = originY + dirAy * minProj + perpY * avgPerpOffset;
      const x2 = originX + dirAx * maxProj + perpX * avgPerpOffset;
      const y2 = originY + dirAy * maxProj + perpY * avgPerpOffset;

      result.push({ x1, y1, x2, y2, votes: totalVotes });
    }

    return result;
  }

  /**
   * Connect lines with nearby endpoints that have similar angles.
   * Keeps the outer endpoints to create a single longer line.
   * Runs iteratively until no more connections occur or maxIterations is reached.
   */
  private connectNearbyEndpoints(
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>,
    angleThresholdDeg: number,
    perpDistThreshold: number,
    lineDistThreshold: number,
    maxIterations: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (lines.length < 2) return lines;

    let currentLines = [...lines];

    for (let iteration = 0; iteration < maxIterations; iteration++) {
      const result = this.connectNearbyEndpointsPass(currentLines, angleThresholdDeg, perpDistThreshold, lineDistThreshold);

      // If no connections happened (same number of lines), we're done
      if (result.length === currentLines.length) {
        return result;
      }

      currentLines = result;
    }

    return currentLines;
  }

  /**
   * Single pass of connecting nearby endpoints.
   * Looks for line pairs where an endpoint of one is close to an endpoint of another.
   */
  private connectNearbyEndpointsPass(
    lines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }>,
    angleThresholdDeg: number,
    perpDistThreshold: number,
    lineDistThreshold: number,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (lines.length < 2) return lines;

    const angleThresh = angleThresholdDeg * (Math.PI / 180);
    const result: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];
    const used = new Array(lines.length).fill(false);

    // Sort by length (descending) to prioritize longer lines
    const sortedIndices = lines
      .map((_, i) => i)
      .sort((a, b) => {
        const lenA = Math.sqrt((lines[a].x2 - lines[a].x1) ** 2 + (lines[a].y2 - lines[a].y1) ** 2);
        const lenB = Math.sqrt((lines[b].x2 - lines[b].x1) ** 2 + (lines[b].y2 - lines[b].y1) ** 2);
        return lenB - lenA;
      });

    for (const i of sortedIndices) {
      if (used[i]) continue;

      const lineA = lines[i];
      used[i] = true;

      // Compute line A's direction and length
      const dxA = lineA.x2 - lineA.x1;
      const dyA = lineA.y2 - lineA.y1;
      const lenA = Math.sqrt(dxA * dxA + dyA * dyA);
      if (lenA < 1) {
        result.push({ ...lineA });
        continue;
      }

      const dirAx = dxA / lenA;
      const dirAy = dyA / lenA;
      const angleA = Math.atan2(dyA, dxA);

      // Line A endpoints
      const endpointsA = [
        { x: lineA.x1, y: lineA.y1, isStart: true },
        { x: lineA.x2, y: lineA.y2, isStart: false },
      ];

      // Collect all endpoints that will form the merged line
      const allEndpoints: Array<{ x: number; y: number }> = [
        { x: lineA.x1, y: lineA.y1 },
        { x: lineA.x2, y: lineA.y2 },
      ];
      let totalVotes = lineA.votes;

      // Find all lines that should connect with lineA
      for (const j of sortedIndices) {
        if (used[j]) continue;

        const lineB = lines[j];

        // Check angle similarity
        const dxB = lineB.x2 - lineB.x1;
        const dyB = lineB.y2 - lineB.y1;
        const angleB = Math.atan2(dyB, dxB);

        let angleDiff = Math.abs(angleA - angleB);
        // Normalize angle difference: atan2 returns [-π, π], so diff can be up to 2π
        // First bring to [0, π] range
        if (angleDiff > Math.PI) angleDiff = 2 * Math.PI - angleDiff;
        // Then normalize for opposite directions (lines can point in opposite directions)
        // A line at 0° and 180° should have 0° difference
        if (angleDiff > Math.PI / 2) angleDiff = Math.PI - angleDiff;

        if (angleDiff > angleThresh) continue;

        // Line B endpoints
        const endpointsB = [
          { x: lineB.x1, y: lineB.y1 },
          { x: lineB.x2, y: lineB.y2 },
        ];

        // Check all endpoint pairs (A endpoints vs B endpoints)
        let shouldConnect = false;

        for (const epA of endpointsA) {
          for (const epB of endpointsB) {
            // Vector from epA to epB
            const dx = epB.x - epA.x;
            const dy = epB.y - epA.y;

            // Perpendicular distance (cross product with line direction)
            const perpDist = Math.abs(dx * -dirAy + dy * dirAx);

            // Distance along line direction (dot product)
            const lineDist = Math.abs(dx * dirAx + dy * dirAy);

            if (perpDist <= perpDistThreshold && lineDist <= lineDistThreshold) {
              shouldConnect = true;
              break;
            }
          }
          if (shouldConnect) break;
        }

        if (shouldConnect) {
          // Add line B's endpoints
          allEndpoints.push({ x: lineB.x1, y: lineB.y1 });
          allEndpoints.push({ x: lineB.x2, y: lineB.y2 });
          totalVotes += lineB.votes;
          used[j] = true;
        }
      }

      // Create merged line from collected endpoints
      if (allEndpoints.length === 2) {
        // No connections happened, keep original line
        result.push({ ...lineA });
      } else {
        // Use the primary line's (lineA) direction to preserve the angle
        // This ensures connected lines maintain the original line's direction
        // Only the length/endpoints are extended

        // Calculate centroid of all endpoints
        let centroidX = 0,
          centroidY = 0;
        for (const p of allEndpoints) {
          centroidX += p.x;
          centroidY += p.y;
        }
        centroidX /= allEndpoints.length;
        centroidY /= allEndpoints.length;

        // Use lineA's normalized direction (already computed as dirAx, dirAy)
        // Project all endpoints onto this direction to find extremes
        let minProj = Infinity;
        let maxProj = -Infinity;

        for (const p of allEndpoints) {
          const proj = (p.x - centroidX) * dirAx + (p.y - centroidY) * dirAy;
          if (proj < minProj) minProj = proj;
          if (proj > maxProj) maxProj = proj;
        }

        // Create merged line using the primary line's direction
        const x1 = centroidX + dirAx * minProj;
        const y1 = centroidY + dirAy * minProj;
        const x2 = centroidX + dirAx * maxProj;
        const y2 = centroidY + dirAy * maxProj;

        result.push({ x1, y1, x2, y2, votes: totalVotes });
      }
    }

    return result;
  }

  /**
   * Verify line quality by checking edge pixel coverage
   */
  private verifyLineQuality(edges: Uint8ClampedArray, width: number, height: number, x1: number, y1: number, x2: number, y2: number): number {
    const dx = x2 - x1;
    const dy = y2 - y1;
    const length = Math.sqrt(dx * dx + dy * dy);
    const steps = Math.ceil(length);
    const tolerance = 3;

    let maxContinuousSegment = 0;
    let currentSegmentLength = 0;

    for (let i = 0; i <= steps; i++) {
      const t = i / steps;
      const x = Math.round(x1 + t * dx);
      const y = Math.round(y1 + t * dy);

      let hasEdge = false;
      for (let dy = -tolerance; dy <= tolerance; dy++) {
        for (let dx = -tolerance; dx <= tolerance; dx++) {
          const checkX = x + dx;
          const checkY = y + dy;

          if (checkX >= 0 && checkX < width && checkY >= 0 && checkY < height) {
            if (edges[checkY * width + checkX] > 0) {
              hasEdge = true;
              break;
            }
          }
        }
        if (hasEdge) break;
      }

      if (hasEdge) {
        currentSegmentLength++;
      } else {
        if (currentSegmentLength > maxContinuousSegment) {
          maxContinuousSegment = currentSegmentLength;
        }
        currentSegmentLength = 0;
      }
    }

    if (currentSegmentLength > maxContinuousSegment) {
      maxContinuousSegment = currentSegmentLength;
    }

    return maxContinuousSegment / steps;
  }

  /**
   * Calculate distance from point to line
   */
  private pointToLineDistance(p1: { x: number; y: number }, p2: { x: number; y: number }, p: { x: number; y: number }): number {
    const dx = p2.x - p1.x;
    const dy = p2.y - p1.y;
    const lengthSquared = dx * dx + dy * dy;

    if (lengthSquared === 0) return Math.sqrt((p.x - p1.x) ** 2 + (p.y - p1.y) ** 2);

    const t = Math.max(0, Math.min(1, ((p.x - p1.x) * dx + (p.y - p1.y) * dy) / lengthSquared));
    const projX = p1.x + t * dx;
    const projY = p1.y + t * dy;

    return Math.sqrt((p.x - projX) ** 2 + (p.y - projY) ** 2);
  }

  /**
   * Fit a line through points using least squares
   */
  private fitLineLeastSquares(points: Array<{ x: number; y: number }>): { point: { x: number; y: number }; direction: { x: number; y: number } } | null {
    if (points.length < 2) return null;

    let sumX = 0;
    let sumY = 0;
    for (const p of points) {
      sumX += p.x;
      sumY += p.y;
    }
    const centroidX = sumX / points.length;
    const centroidY = sumY / points.length;

    let cxx = 0;
    let cxy = 0;
    let cyy = 0;
    for (const p of points) {
      const dx = p.x - centroidX;
      const dy = p.y - centroidY;
      cxx += dx * dx;
      cxy += dx * dy;
      cyy += dy * dy;
    }

    const trace = cxx + cyy;
    const det = cxx * cyy - cxy * cxy;
    const lambda1 = trace / 2 + Math.sqrt((trace * trace) / 4 - det);

    let dirX: number, dirY: number;
    if (Math.abs(cxy) > 1e-10) {
      dirX = lambda1 - cyy;
      dirY = cxy;
    } else if (cxx > cyy) {
      dirX = 1;
      dirY = 0;
    } else {
      dirX = 0;
      dirY = 1;
    }

    const dirLength = Math.sqrt(dirX * dirX + dirY * dirY);
    if (dirLength < 1e-10) return null;

    return {
      point: { x: centroidX, y: centroidY },
      direction: { x: dirX / dirLength, y: dirY / dirLength },
    };
  }

  /**
   * Fit a line through points using RANSAC to robustly handle outliers
   * (Adapted from PolygonProcessor for better line fitting accuracy)
   * @param points Points to fit
   * @param distanceThreshold Maximum distance from line for a point to be considered an inlier (pixels)
   * @param maxIterations Maximum number of RANSAC iterations
   * @param minInlierRatio Minimum ratio of inliers required for a valid fit (0-1)
   */
  private fitLineRANSAC(points: Array<{ x: number; y: number }>, distanceThreshold = 2.0, maxIterations = 100, minInlierRatio = 0.6): { point: { x: number; y: number }; direction: { x: number; y: number }; inlierCount: number } | null {
    if (points.length < 2) return null;

    const minInliers = Math.max(2, Math.ceil(points.length * minInlierRatio));

    let bestInliers: Array<{ x: number; y: number }> = [];
    let bestModel: { point: { x: number; y: number }; direction: { x: number; y: number } } | null = null;

    // RANSAC iterations
    for (let iter = 0; iter < maxIterations; iter++) {
      // Randomly sample 2 points
      const idx1 = Math.floor(Math.random() * points.length);
      let idx2 = Math.floor(Math.random() * points.length);
      while (idx2 === idx1) {
        idx2 = Math.floor(Math.random() * points.length);
      }

      const p1 = points[idx1];
      const p2 = points[idx2];

      // Create line model from these two points
      const dx = p2.x - p1.x;
      const dy = p2.y - p1.y;
      const length = Math.sqrt(dx * dx + dy * dy);

      if (length < 1e-6) continue; // Degenerate case

      const direction = { x: dx / length, y: dy / length };
      const point = { x: (p1.x + p2.x) / 2, y: (p1.y + p2.y) / 2 };

      // Count inliers (points within distance threshold)
      const inliers: Array<{ x: number; y: number }> = [];
      for (const pt of points) {
        const distance = this.pointToInfiniteLineDistance(pt, point, direction);
        if (distance <= distanceThreshold) {
          inliers.push(pt);
        }
      }

      // Update best model if this has more inliers
      if (inliers.length > bestInliers.length) {
        bestInliers = inliers;
        bestModel = { point, direction };
      }

      // Early termination if we have enough inliers
      if (bestInliers.length >= points.length * 0.9) {
        break;
      }
    }

    // Check if we have enough inliers
    if (bestInliers.length < minInliers || !bestModel) {
      return null;
    }

    // Re-fit line using all inliers for better accuracy
    const finalFit = this.fitLineLeastSquares(bestInliers);
    if (!finalFit) {
      return { ...bestModel, inlierCount: bestInliers.length };
    }

    return {
      point: finalFit.point,
      direction: finalFit.direction,
      inlierCount: bestInliers.length,
    };
  }

  /**
   * Calculate perpendicular distance from a point to an infinite line
   */
  private pointToInfiniteLineDistance(point: { x: number; y: number }, linePoint: { x: number; y: number }, lineDir: { x: number; y: number }): number {
    // Vector from line point to the point
    const toPointX = point.x - linePoint.x;
    const toPointY = point.y - linePoint.y;

    // Project onto line direction
    const projLength = toPointX * lineDir.x + toPointY * lineDir.y;

    // Calculate closest point on line
    const closestX = linePoint.x + lineDir.x * projLength;
    const closestY = linePoint.y + lineDir.y * projLength;

    // Return distance
    return Math.sqrt((point.x - closestX) ** 2 + (point.y - closestY) ** 2);
  }

  /**
   * Get registered processor IDs for UI
   */
  getRegisteredProcessorIds(): string[] {
    return super.getRegisteredProcessorIds();
  }

  // ==================== Pixel Alignment Methods (from PolygonProcessor) ====================

  /**
   * Find the density peak along a scan line in the density mask.
   * Scans perpendicular to the line and finds the pixel with highest intensity.
   * (Identical to PolygonProcessor.findDensityPeakAlongScanLine)
   *
   * @param samplePoint The starting point on the line
   * @param perpDir The perpendicular direction to scan along
   * @param searchRange How far to scan in each direction
   * @param densityMask The density mask image data
   * @returns The pixel position of the density peak, or null if no significant peak found
   */
  private findDensityPeakAlongScanLine(samplePoint: { x: number; y: number }, perpDir: { x: number; y: number }, searchRange: number, densityMask: ImageData): { x: number; y: number } | null {
    const intensityThreshold = 30; // Minimum intensity to consider as part of a line/feature
    const smoothingRadius = 2; // Gaussian-like smoothing window radius

    // Build intensity profile along the scan line
    const intensityProfile: Array<{
      x: number;
      y: number;
      offset: number;
      intensity: number;
    }> = [];

    for (let offset = -searchRange; offset <= searchRange; offset++) {
      const scanX = samplePoint.x + perpDir.x * offset;
      const scanY = samplePoint.y + perpDir.y * offset;
      const pixelX = Math.round(scanX);
      const pixelY = Math.round(scanY);

      if (pixelX < 0 || pixelX >= densityMask.width || pixelY < 0 || pixelY >= densityMask.height) {
        intensityProfile.push({ x: pixelX, y: pixelY, offset, intensity: 0 });
        continue;
      }

      const idx = (pixelY * densityMask.width + pixelX) * 4;
      const r = densityMask.data[idx];
      const g = densityMask.data[idx + 1];
      const b = densityMask.data[idx + 2];
      const intensity = (r + g + b) / 3;

      intensityProfile.push({ x: pixelX, y: pixelY, offset, intensity });
    }

    if (intensityProfile.length === 0) {
      return null;
    }

    // Smooth the intensity profile using a weighted moving average (triangle kernel)
    const smoothedIntensities: number[] = [];
    for (let i = 0; i < intensityProfile.length; i++) {
      let weightedSum = 0;
      let totalWeight = 0;

      for (let j = -smoothingRadius; j <= smoothingRadius; j++) {
        const idx = i + j;
        if (idx >= 0 && idx < intensityProfile.length) {
          // Triangle kernel weight: higher weight for closer samples
          const weight = smoothingRadius + 1 - Math.abs(j);
          weightedSum += intensityProfile[idx].intensity * weight;
          totalWeight += weight;
        }
      }

      smoothedIntensities.push(totalWeight > 0 ? weightedSum / totalWeight : 0);
    }

    // Find the global maximum on the smoothed profile (above threshold)
    let maxSmoothedIntensity = 0;
    let maxIdx = -1;

    for (let i = 0; i < smoothedIntensities.length; i++) {
      if (smoothedIntensities[i] > maxSmoothedIntensity && smoothedIntensities[i] >= intensityThreshold) {
        maxSmoothedIntensity = smoothedIntensities[i];
        maxIdx = i;
      }
    }

    if (maxIdx === -1) {
      return null;
    }

    // Find all peaks (local maxima) on the smoothed profile
    const peaks: Array<{ idx: number; intensity: number }> = [];
    for (let i = 1; i < smoothedIntensities.length - 1; i++) {
      if (smoothedIntensities[i] >= intensityThreshold && smoothedIntensities[i] >= smoothedIntensities[i - 1] && smoothedIntensities[i] >= smoothedIntensities[i + 1]) {
        peaks.push({ idx: i, intensity: smoothedIntensities[i] });
      }
    }

    // If no local maxima found, use global max
    if (peaks.length === 0) {
      peaks.push({ idx: maxIdx, intensity: maxSmoothedIntensity });
    }

    // Select best peak: prefer highest intensity, use distance to edge (offset=0) as tiebreaker
    const significanceThreshold = 0.6;
    const maxPeakIntensity = Math.max(...peaks.map((p) => p.intensity));
    const strongPeaks = peaks.filter((p) => p.intensity >= maxPeakIntensity * significanceThreshold);

    let selectedPeakIdx: number;
    if (strongPeaks.length === 1) {
      selectedPeakIdx = strongPeaks[0].idx;
    } else {
      // Multiple strong peaks - pick the one closest to the original edge position (offset=0)
      // The center of the profile corresponds to offset=0
      const centerIdx = Math.floor(intensityProfile.length / 2);
      selectedPeakIdx = strongPeaks.reduce((closest, peak) => (Math.abs(peak.idx - centerIdx) < Math.abs(closest.idx - centerIdx) ? peak : closest)).idx;
    }

    // Return the pixel position at the selected peak
    const selectedSample = intensityProfile[selectedPeakIdx];
    return { x: selectedSample.x, y: selectedSample.y };
  }

  /**
   * Refine a line using pixel alignment (identical to PolygonProcessor's applyPixelAlignmentSinglePass).
   * Uses density mask to find peak positions along perpendicular scan lines,
   * then fits a line through the peaks using RANSAC.
   *
   * @param line The line to refine
   * @param densityMask The density mask (captured image) for finding density peaks
   * @param positionMap Optional position map for 12-bit XY decoding
   * @param searchRange Search range in pixels for perpendicular scans
   * @param minProbeRatio Minimum ratio of probes that must find a peak (0-1)
   * @returns Refined line with updated endpoints, or original line if refinement fails
   */
  private refineLinePixelAlignment(
    line: { x1: number; y1: number; x2: number; y2: number; votes: number },
    densityMask: ImageData,
    positionMap: ImageData | undefined,
    searchRange: number,
    minProbeRatio = 0.7,
  ): {
    line: { x1: number; y1: number; x2: number; y2: number; votes: number };
    peakData: Array<{ x: number; y: number; index: number }>;
  } {
    const dx = line.x2 - line.x1;
    const dy = line.y2 - line.y1;
    const length = Math.sqrt(dx * dx + dy * dy);

    if (length < 10) {
      return { line, peakData: [] };
    }

    const lineDir = { x: dx / length, y: dy / length };
    const perpDir = { x: -lineDir.y, y: lineDir.x }; // 90 degree rotation

    // Sample points along the line (similar to PolygonProcessor)
    const paddingRatio = 0.15; // 15% padding at each end
    const edgePadding = Math.min(length * paddingRatio, searchRange);
    const samplingLength = length - edgePadding;

    if (samplingLength <= 0) {
      return { line, peakData: [] };
    }

    const numSamples = Math.max(5, Math.floor(samplingLength));
    const peakData: Array<{ x: number; y: number; index: number }> = [];

    // Phase 1: Find density peaks for each probe along the line
    for (let j = 0; j < numSamples; j++) {
      const sampleDistance = edgePadding / 2.0 + (j / (numSamples - 1)) * samplingLength;
      const t = sampleDistance / length;
      const samplePoint = {
        x: line.x1 + dx * t,
        y: line.y1 + dy * t,
      };

      // Find density peak along perpendicular scan line
      const densityPeak = this.findDensityPeakAlongScanLine(samplePoint, perpDir, searchRange, densityMask);

      if (densityPeak) {
        // If position map is available, decode 12-bit eye-space XY back to
        // sub-pixel image space — the renderer flips the buffer
        // vertically, so we (1 - normY) the Y axis to keep image-space
        // y=0-at-top semantics.
        if (positionMap) {
          const idx = (densityPeak.y * positionMap.width + densityPeak.x) * 4;
          const r = positionMap.data[idx];
          const g = positionMap.data[idx + 1];
          const a = positionMap.data[idx + 3];

          if (r !== 0 || g !== 0 || a !== 0) {
            const xHigh = r;
            const yHigh = g;
            const xLow = (a >> 4) & 0x0f;
            const yLow = a & 0x0f;
            const x12 = (xHigh << 4) | xLow; // 0-4095
            const y12 = (yHigh << 4) | yLow; // 0-4095

            const xPixel = (x12 / 4095) * densityMask.width;
            const yPixel = (1 - y12 / 4095) * densityMask.height;
            peakData.push({ x: xPixel, y: yPixel, index: j });
          } else {
            peakData.push({ x: densityPeak.x, y: densityPeak.y, index: j });
          }
        } else {
          peakData.push({ x: densityPeak.x, y: densityPeak.y, index: j });
        }
      }
    }

    // Check if we have enough peaks
    if (peakData.length < 3) {
      return { line, peakData: [] };
    }

    // Check minimum peak ratio (matches PolygonProcessor)
    if (peakData.length < numSamples * minProbeRatio) {
      return { line, peakData: [] };
    }

    // Phase 2: Fit a line through the peaks using RANSAC
    const peakPositions = peakData.map((p) => ({ x: p.x, y: p.y }));
    const fitResult = this.fitLineRANSAC(peakPositions, 2.0, 100, 0.3);

    if (!fitResult) {
      return { line, peakData };
    }

    const { point, direction } = fitResult;
    const dirLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
    const normDir = { x: direction.x / dirLength, y: direction.y / dirLength };

    // Project all peaks onto the fitted line to find endpoints
    const projections: number[] = [];
    for (const peak of peakPositions) {
      const toPeak = { x: peak.x - point.x, y: peak.y - point.y };
      const projection = toPeak.x * normDir.x + toPeak.y * normDir.y;
      projections.push(projection);
    }

    const minProj = Math.min(...projections);
    const maxProj = Math.max(...projections);

    const x1 = point.x + normDir.x * minProj;
    const y1 = point.y + normDir.y * minProj;
    const x2 = point.x + normDir.x * maxProj;
    const y2 = point.y + normDir.y * maxProj;

    return {
      line: { x1, y1, x2, y2, votes: line.votes },
      peakData,
    };
  }

  /**
   * Split a line into multiple segments based on gaps in peak coverage.
   * This is the "split" pass that runs after pixel alignment refinement.
   *
   * @param line The refined line
   * @param peakData Peak data from the refinement pass (with index for gap detection)
   * @param minSegmentLength Minimum segment length in pixels
   * @param maxGap Maximum gap in sample indices before splitting (default: 3)
   * @returns Array of line segments
   */
  private splitLineByGaps(
    line: { x1: number; y1: number; x2: number; y2: number; votes: number },
    peakData: Array<{ x: number; y: number; index: number }>,
    minSegmentLength: number,
    maxGap = 3,
  ): Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> {
    if (peakData.length < 3) {
      // Not enough peak data for splitting, return original line if long enough
      const lineLength = Math.sqrt((line.x2 - line.x1) ** 2 + (line.y2 - line.y1) ** 2);
      return lineLength >= minSegmentLength ? [line] : [];
    }

    // Find all continuous segments based on gaps in sample indices
    const segments: Array<{ start: number; end: number }> = [];
    let currentSegmentStart = 0;

    for (let i = 1; i < peakData.length; i++) {
      const gap = peakData[i].index - peakData[i - 1].index;

      // Gap detected - end current segment and start a new one
      if (gap > maxGap) {
        const segmentLength = i - currentSegmentStart;
        if (segmentLength >= 3) {
          segments.push({ start: currentSegmentStart, end: i - 1 });
        }
        currentSegmentStart = i;
      }
    }

    // Add the final segment
    const finalLength = peakData.length - currentSegmentStart;
    if (finalLength >= 3) {
      segments.push({ start: currentSegmentStart, end: peakData.length - 1 });
    }

    // If no valid segments found, return original line if long enough
    if (segments.length === 0) {
      const lineLength = Math.sqrt((line.x2 - line.x1) ** 2 + (line.y2 - line.y1) ** 2);
      return lineLength >= minSegmentLength ? [line] : [];
    }

    // Create a line for each segment using RANSAC fitting
    const resultLines: Array<{ x1: number; y1: number; x2: number; y2: number; votes: number }> = [];

    for (const segment of segments) {
      const continuousPeaks = peakData.slice(segment.start, segment.end + 1);
      const peakPositions = continuousPeaks.map((p) => ({ x: p.x, y: p.y }));

      // Use RANSAC to fit line through peaks
      const fitResult = this.fitLineRANSAC(peakPositions, 2.0, 100, 0.1);
      if (!fitResult) continue;

      const { point, direction } = fitResult;
      const dirLength = Math.sqrt(direction.x * direction.x + direction.y * direction.y);
      const normDir = { x: direction.x / dirLength, y: direction.y / dirLength };

      const projections: number[] = [];
      for (const peak of peakPositions) {
        const toPeak = { x: peak.x - point.x, y: peak.y - point.y };
        const projection = toPeak.x * normDir.x + toPeak.y * normDir.y;
        projections.push(projection);
      }

      const minProj = Math.min(...projections);
      const maxProj = Math.max(...projections);

      const x1 = point.x + normDir.x * minProj;
      const y1 = point.y + normDir.y * minProj;
      const x2 = point.x + normDir.x * maxProj;
      const y2 = point.y + normDir.y * maxProj;

      // Check if segment is long enough
      const segmentLength = Math.sqrt((x2 - x1) ** 2 + (y2 - y1) ** 2);
      if (segmentLength >= minSegmentLength) {
        resultLines.push({ x1, y1, x2, y2, votes: line.votes });
      }
    }

    // If no valid segments after filtering, return original line if long enough
    if (resultLines.length === 0) {
      const lineLength = Math.sqrt((line.x2 - line.x1) ** 2 + (line.y2 - line.y1) ** 2);
      return lineLength >= minSegmentLength ? [line] : [];
    }

    return resultLines;
  }
}
